import React, { useState, useEffect, useRef, useMemo } from "react";
import JsBarcode from "jsbarcode";
import Modal from "../../components/Modal";
import ImageProcessingService from "../../components/ImageProcessingService";
import VariableService from "../../components/VariableService";
import {
  getWooCommerceProductsByIds,
  updateWooCommerceProductAddonsMeta,
  updateWooCommerceProductFeaturedStatus,
  updateWooCommerceProductPricing,
  updateWooCommerceProductStockStatus,
} from "../../components/woocommerceHelper";
import { toast } from "react-toastify";
import Select from "react-select";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { compareProductCreationDescending } from '../../utils/productCreationOrder';

const KOUL_LOGO_URL = "https://d2sz1kgdtrlf1n.cloudfront.net/task_images/gU9c1715161842930-YeloEommerceLogo2029.png";
const GLOVO_LOGO_URL = "https://www.citypng.com/public/uploads/preview/glovo-square-app-logo-icon-png-7017516949682938wikukphx1.png";
const OFFICE_IMAGE_BASE_URL = process.env.REACT_APP_DATA_PIC_PATH || "https://office.le-celadon.ma";
const imageProcessingTypeOptions = ["chatgpt", "gemini"];

const PROMPT_VARIABLE_REGEX = /{{\s*([a-zA-Z0-9_]+)(?:\|type:([a-zA-Z0-9_]+):([^}]+))?\s*}}/g;

const extractPromptTemplate = (rawValue) => {
  if (rawValue === null || rawValue === undefined) return "";

  const normalized = String(rawValue).trim();
  if (!normalized) return "";

  try {
    const parsed = JSON.parse(normalized);
    if (parsed && typeof parsed.prompt === "string") {
      return parsed.prompt.trim();
    }
  } catch (err) {
    // Fall back to raw value when prompt content is not JSON.
  }

  return normalized;
};

const parsePromptVariableDefinitions = (rawValue) => {
  const template = extractPromptTemplate(rawValue);
  if (!template) return [];

  const matches = [...template.matchAll(PROMPT_VARIABLE_REGEX)];
  const seen = new Set();

  return matches.reduce((result, match) => {
    const [, key, type, defaultValue] = match;
    const normalizedKey = key ? String(key).trim() : "";
    if (!normalizedKey || seen.has(normalizedKey)) {
      return result;
    }

    seen.add(normalizedKey);
    result.push({
      key: normalizedKey,
      label: normalizedKey,
      type: type ? String(type).trim() : "string",
      default_value: defaultValue ? String(defaultValue).trim() : "",
      value: "",
    });
    return result;
  }, []);
};

const normalizePromptVariableEntries = (definitions = [], currentEntries = []) => {
  const currentByKey = new Map(
    (Array.isArray(currentEntries) ? currentEntries : [])
      .filter((entry) => entry && entry.key)
      .map((entry) => [
        String(entry.key).trim(),
        {
          key: String(entry.key || "").trim(),
          label: String(entry.label || "").trim(),
          type: String(entry.type || "string").trim(),
          default_value: String(entry.default_value || "").trim(),
          value: String(entry.value || "").trim(),
        },
      ])
  );

  return (Array.isArray(definitions) ? definitions : []).map((definition) => {
    const key = String(definition?.key || "").trim();
    const existing = currentByKey.get(key);

    return {
      key,
      label: String(definition?.label || key).trim(),
      type: String(definition?.type || existing?.type || "string").trim(),
      default_value: String(definition?.default_value || existing?.default_value || "").trim(),
      value: String(existing?.value || definition?.value || "").trim(),
    };
  });
};

const arePromptVariablesEqual = (left = [], right = []) => {
  if (left === right) return true;
  if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;

  return left.every((entry, index) => {
    const other = right[index];
    return (
      String(entry?.key || "") === String(other?.key || "") &&
      String(entry?.label || "") === String(other?.label || "") &&
      String(entry?.type || "") === String(other?.type || "") &&
      String(entry?.default_value || "") === String(other?.default_value || "") &&
      String(entry?.value || "") === String(other?.value || "")
    );
  });
};

const ProductTable = ({
  products,
  fullProducts = [],
  productCategories,
  stores,
  siteHandleModal,
  handleWooCategoryEdit,
  setCurrentProduct,
  selectedCategory,
  updateGlobalAvailability,
  updateAvailability,
  handleEditClick,
  handleCloneProduct,
  handleDelete,
  currentPage,
  setCurrentPage,
  serverPagination = false,
  totalPages: serverTotalPages,
  isLoading = false,
  setSelectedProducts,
  selectedProducts,
  dynfilter,
  handleComponent,
  plateformFilters,
  componentFilter,
  onToggleIsComponent,
  onAddStock,
  onPrintBarcode,
  onAddToKooul,
  onAddToAimer,
  onManageAimerStatus,
  onToggleGlovoAvailability,
  onManageStoreExpress,
  onOpenPublierPlateformes,
  kooulPlateforms = [],
  aimerPlateforms = [],
  glovoPlateforms = [],
  onLoadAllProducts,
  deliveryZoneOptions = [],
  onUpdateWebsiteDelivery,
  onUpdateVaseInclusion,
  productAddons = [],
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("table");
  const [modalImage, setModalImage] = useState(null);
  const [modalImages, setModalImages] = useState([]);
  const [variables, setVariables] = useState([]);
  const [selectedVariableIds, setSelectedVariableIds] = useState([]);
  const [processProduct, setProcessProduct] = useState(null);
  const [additionnalPrompt, setAdditionnalPrompt] = useState("");
  const [imageProcessingType, setImageProcessingType] = useState("chatgpt");
  const [componentToggleLoading, setComponentToggleLoading] = useState({});
  const [availabilityToggleLoading, setAvailabilityToggleLoading] = useState({});
  const [wooStatusByProductId, setWooStatusByProductId] = useState({});
  const [actionsModalProduct, setActionsModalProduct] = useState(null);
  const [pluModalOpen, setPluModalOpen] = useState(false);
  const [pluModalProduct, setPluModalProduct] = useState(null);
  const [pluCategories, setPluCategories] = useState([]);
  const [selectedPluCategory, setSelectedPluCategory] = useState(null);
  const [useCustomPluPrice, setUseCustomPluPrice] = useState(false);
  const [customPluPrice, setCustomPluPrice] = useState("");
  const [websiteDeliveryDrafts, setWebsiteDeliveryDrafts] = useState({});
  const [websiteDeliverySaving, setWebsiteDeliverySaving] = useState({});
  const [websiteDiscountProduct, setWebsiteDiscountProduct] = useState(null);
  const [websiteDiscountForm, setWebsiteDiscountForm] = useState({
    regularPrice: "",
    percentage: "",
    salePrice: "",
  });
  const [websiteDiscountSaving, setWebsiteDiscountSaving] = useState(false);
  const [websiteAddonsProduct, setWebsiteAddonsProduct] = useState(null);
  const [selectedWebsiteAddonIds, setSelectedWebsiteAddonIds] = useState([]);
  const [websiteAddonsSaving, setWebsiteAddonsSaving] = useState(false);
  const [vaseSaving, setVaseSaving] = useState({});
  const pluBarcodeRef = useRef(null);

  const itemsPerPage = 20;
  const barcodeRefs = useRef({});
  const saveVaseInclusion = async (product, value) => {
    const productId = String(product?._id || product?.id || '');
    setVaseSaving((previous) => ({ ...previous, [productId]: true }));
    try {
      await onUpdateVaseInclusion(product, value);
    } catch (error) {
      console.error('Vase inclusion update failed', error);
      toast.error('Impossible de confirmer la mise à jour du vase.');
    } finally {
      setVaseSaving((previous) => ({ ...previous, [productId]: false }));
    }
  };
  const renderVaseSelector = (product) => {
    const productId = String(product?._id || product?.id || '');
    return (
      <div className="bg-light text-dark rounded-2 p-2 mb-2" style={{ maxWidth: 220 }}>
        <label className="small fw-bold d-block mb-1" htmlFor={`vase-${productId}`}>Vase sur la photo</label>
        <select
          id={`vase-${productId}`}
          className="form-select form-select-sm"
          aria-label={`Vase pour ${product.name || 'ce produit'}`}
          value={product.vase_inclusion || 'unspecified'}
          disabled={Boolean(vaseSaving[productId]) || typeof onUpdateVaseInclusion !== 'function'}
          onChange={(event) => saveVaseInclusion(product, event.target.value)}
        >
          <option value="unspecified">Non renseigné (aucun badge)</option>
          <option value="included">Vase inclus</option>
          <option value="not_included">Vase non inclus</option>
        </select>
        {product.woocommerce_id && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary mt-1"
            disabled={Boolean(vaseSaving[productId]) || typeof onUpdateVaseInclusion !== 'function'}
            onClick={() => saveVaseInclusion(product, product.vase_inclusion || 'unspecified')}
          >
            Synchroniser le badge site
          </button>
        )}
        {vaseSaving[productId] && <small>Enregistrement…</small>}
      </div>
    );
  };
  const maxPagesToShow = 5;

  const parsePrice = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number.parseFloat(String(value).replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
  };

  const formatPrice = (value) => {
    const parsed = parsePrice(value);
    return parsed === null ? null : `${parsed.toFixed(2)} DH`;
  };

  const formatPriceValue = (value) => {
    const parsed = parsePrice(value);
    return parsed === null ? "" : parsed.toFixed(2);
  };

  const calculateSalePriceFromPercent = (regularPrice, percentage) => {
    const regular = parsePrice(regularPrice);
    const percent = parsePrice(percentage);
    if (regular === null || percent === null) return "";
    const boundedPercent = Math.min(Math.max(percent, 0), 100);
    return Math.max(regular - (regular * boundedPercent / 100), 0).toFixed(2);
  };

  const calculateDiscountPercent = (regularPrice, salePrice) => {
    const regular = parsePrice(regularPrice);
    const sale = parsePrice(salePrice);
    if (regular === null || regular <= 0 || sale === null || sale <= 0 || sale >= regular) return "";
    return (((regular - sale) / regular) * 100).toFixed(2);
  };

  const readPegasusAddonsMeta = (remoteProduct) => {
    const meta = Array.isArray(remoteProduct?.meta_data) ? remoteProduct.meta_data : [];
    const entry = meta.find((item) => item && item.key === "_pegasus_product_addons");
    if (!entry?.value) return [];
    try {
      const parsed = typeof entry.value === "string" ? JSON.parse(entry.value) : entry.value;
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  };

  const normalizeWebsiteDelivery = (value = {}) => {
    const expressEnabled = value?.express_enabled === true || value?.express_enabled === "1" || value?.express_enabled === 1;
    const moroccoEnabled = value?.morocco_enabled === true || value?.morocco_enabled === "1" || value?.morocco_enabled === 1;
    const expressZones = expressEnabled && Array.isArray(value?.express_zones)
      ? value.express_zones
          .map((zone) => ({
            code: String(zone?.code || zone?.value || "").trim().toUpperCase(),
            name: String(zone?.name || zone?.label || "").trim(),
          }))
          .filter((zone) => zone.code)
      : [];

    return {
      express_enabled: expressEnabled,
      express_zones: expressZones,
      morocco_enabled: moroccoEnabled,
    };
  };

  const renderWebsiteDeliveryBadges = (product) => {
    const delivery = normalizeWebsiteDelivery(product?.website_delivery);
    if (!delivery.express_enabled && !delivery.morocco_enabled) return null;
    const zoneLabel = delivery.express_zones
      .map((zone) => zone.name || zone.code)
      .filter(Boolean)
      .join(", ");

    return (
      <>
        {delivery.express_enabled && (
          <span className="badge bg-danger-subtle text-danger border border-danger-subtle">
            <i className="fa-solid fa-bolt me-1"></i>
            Express{zoneLabel ? `: ${zoneLabel}` : ""}
          </span>
        )}
        {delivery.morocco_enabled && (
          <span className="badge bg-success-subtle text-success border border-success-subtle">
            <i className="fa-solid fa-map-location-dot me-1"></i>
            Maroc
          </span>
        )}
      </>
    );
  };

  const websiteDeliveryKey = (product) => normalizeId(product?._id || product?.id);

  const getWebsiteDeliveryDraft = (product) => {
    const key = websiteDeliveryKey(product);
    return normalizeWebsiteDelivery(websiteDeliveryDrafts[key] || product?.website_delivery);
  };

  const setWebsiteDeliveryDraft = (product, patch) => {
    const key = websiteDeliveryKey(product);
    setWebsiteDeliveryDrafts((prev) => {
      const current = normalizeWebsiteDelivery(prev[key] || product?.website_delivery);
      const next = normalizeWebsiteDelivery({ ...current, ...patch });
      if (!next.express_enabled) {
        next.express_zones = [];
      }
      return { ...prev, [key]: next };
    });
  };

  const areWebsiteDeliveriesEqual = (left, right) =>
    JSON.stringify(normalizeWebsiteDelivery(left)) === JSON.stringify(normalizeWebsiteDelivery(right));

  const saveWebsiteDeliveryDraft = async (product) => {
    if (typeof onUpdateWebsiteDelivery !== "function") {
      toast.error("Sauvegarde livraison indisponible.");
      return;
    }
    const key = websiteDeliveryKey(product);
    const nextDelivery = getWebsiteDeliveryDraft(product);
    setWebsiteDeliverySaving((prev) => ({ ...prev, [key]: true }));
    try {
      await onUpdateWebsiteDelivery(product, nextDelivery);
      setWebsiteDeliveryDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } finally {
      setWebsiteDeliverySaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  const saveWebsiteDeliveryDirect = async (product, nextDelivery) => {
    if (typeof onUpdateWebsiteDelivery !== "function") {
      toast.error("Sauvegarde livraison indisponible.");
      return;
    }
    const key = websiteDeliveryKey(product);
    setWebsiteDeliverySaving((prev) => ({ ...prev, [key]: true }));
    try {
      await onUpdateWebsiteDelivery(product, normalizeWebsiteDelivery(nextDelivery));
      setWebsiteDeliveryDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (error) {
      console.error("Website delivery direct save error:", error);
      toast.error("Erreur lors de la mise à jour livraison.");
    } finally {
      setWebsiteDeliverySaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  const normalizeId = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "object") {
      return (
        value.$oid ||
        value._id ||
        value.id ||
        value.value ||
        value.plateform_id ||
        value
      );
    }
    return String(value);
  };

  const kooulPlateformIds = useMemo(
    () =>
      (Array.isArray(kooulPlateforms) ? kooulPlateforms : [])
        .filter((plateform) => {
          const name = `${plateform?.name ?? ""}`.toLowerCase();
          const type = `${plateform?.type ?? ""}`.toLowerCase();
          return type === "kooul" || name.startsWith("kooul_");
        })
        .map((plateform) => normalizeId(plateform._id || plateform.id || plateform.plateform_id))
        .filter(Boolean),
    [kooulPlateforms]
  );

  const glovoPlateformIds = useMemo(
    () =>
      new Set(
        (Array.isArray(glovoPlateforms) ? glovoPlateforms : [])
          .filter((plateform) => {
            const t = `${plateform?.type ?? ""}`.toLowerCase();
            return t === "glovo" || t === "glovo_sisters";
          })
          .map((plateform) => normalizeId(plateform._id || plateform.id || plateform.plateform_id))
          .filter(Boolean)
      ),
    [glovoPlateforms]
  );

  const isGlovoFamilyPlateform = (plateform) => {
    const type = `${plateform?.type ?? plateform?.additionnal_data?.type ?? plateform?.additional_data?.type ?? ""}`.toLowerCase();
    const name = `${plateform?.name ?? plateform?.additionnal_data?.name ?? plateform?.additional_data?.name ?? ""}`.toLowerCase();
    return (
      type === "glovo" ||
      type === "glovo_sisters" ||
      name.includes("glovo")
    );
  };

  const getProductGlovoFamilyPlateformIds = (product) =>
    new Set(
      (Array.isArray(product?.plateforms) ? product.plateforms : [])
        .filter((entry) => {
          if (isGlovoFamilyPlateform(entry)) return true;
          const entryPlateformId = normalizeId(entry?.plateform_id || entry?._id || entry?.id || entry?.value);
          if (!entryPlateformId) return false;
          const fullPlateform = (Array.isArray(glovoPlateforms) ? glovoPlateforms : []).find(
            (plateform) => normalizeId(plateform?._id || plateform?.id || plateform?.plateform_id || plateform?.value) === entryPlateformId
          );
          return isGlovoFamilyPlateform(fullPlateform);
        })
        .map((entry) => normalizeId(entry?.plateform_id || entry?._id || entry?.id || entry?.value))
        .filter(Boolean)
    );

  const aimerPlateformIds = useMemo(
    () =>
      (Array.isArray(aimerPlateforms) ? aimerPlateforms : [])
        .filter((plateform) => {
          const name = `${plateform?.name ?? ""}`.toLowerCase();
          const type = `${plateform?.type ?? ""}`.toLowerCase();
          return type === "aimer" || name.includes("aimer");
        })
        .map((plateform) => normalizeId(plateform._id || plateform.id || plateform.plateform_id))
        .filter(Boolean),
    [aimerPlateforms]
  );

  // Fetch PLU categories on component mount
  useEffect(() => {
    const fetchPluCategories = async () => {
      try {
        const pluVars = await VariableService.listVariables('plu');
      
        
        if (Array.isArray(pluVars) && pluVars.length > 0) {
          // Find PLU Carrefours variable (trim spaces for matching)
          const pluVar = pluVars.find(v => 
            v && v.name && v.name.trim().toLowerCase().includes('plu carrefour')
          );
          
        
          
          if (pluVar && pluVar.value) {
            try {
              // Parse the JSON string value
              const categories = JSON.parse(pluVar.value);
          
              
              if (Array.isArray(categories)) {
                setPluCategories(categories);
                toast.success(`${categories.length} catégories PLU chargées avec succès`);
              } else {
                console.error('PLU categories is not an array', categories);
                setPluCategories([]);
                toast.warning('Format de catégories PLU invalide');
              }
            } catch (e) {
              console.error('Failed to parse PLU categories JSON', e, pluVar.value);
              toast.error('Erreur lors de l\'analyse des catégories PLU');
              setPluCategories([]);
            }
          } else {
            console.warn('No PLU Carrefours variable found or value is empty');
            toast.warning('Aucune configuration PLU Carrefours trouvée');
            setPluCategories([]);
          }
        } else {
          console.warn('No PLU variables found');
          toast.info('Aucune variable PLU configurée');
          setPluCategories([]);
        }
      } catch (error) {
        console.error('Failed to fetch PLU variables', error);
        toast.error('Erreur lors du chargement des variables PLU');
        setPluCategories([]);
      }
    };
    fetchPluCategories();
  }, []);

  const openPluModal = (product) => {
    const price = parseFloat(product.sale_price || product.regular_price || 0);
    if (price >= 1000) {
      toast.error('PLU Carrefour ne fonctionne que pour les produits avec un prix < 1000 DH');
      return;
    }
    setPluModalProduct(product);
    setSelectedPluCategory(null);
    setUseCustomPluPrice(false);
    setCustomPluPrice(price.toFixed(2));
    setPluModalOpen(true);
  };

  const closePluModal = () => {
    setPluModalOpen(false);
    setPluModalProduct(null);
    setSelectedPluCategory(null);
    setUseCustomPluPrice(false);
    setCustomPluPrice("");
  };

  const closeBootstrapModal = (modalId) => {
    try {
      const modalElement = document.getElementById(modalId);
      if (!modalElement) return;
      const modal = window.bootstrap?.Modal?.getInstance(modalElement);
      if (modal) {
        modal.hide();
        return;
      }
    } catch (error) {
      // Fallback below handles non-Bootstrap environments.
    }

    document.querySelector(`#${modalId} [data-bs-dismiss="modal"]`)?.click();
  };

  const getEffectivePluPrice = () => {
    if (!pluModalProduct) {
      return 0;
    }

    const defaultPrice = parseFloat(pluModalProduct.sale_price || pluModalProduct.regular_price || 0);
    if (!useCustomPluPrice) {
      return defaultPrice;
    }

    const parsedCustomPrice = parseFloat(customPluPrice);
    if (!Number.isFinite(parsedCustomPrice)) {
      return NaN;
    }

    return parsedCustomPrice;
  };

  const generatePluBarcode = () => {
    if (!selectedPluCategory || !pluModalProduct) {
      toast.error('Veuillez sélectionner une catégorie PLU');
      return;
    }

    const price = getEffectivePluPrice();
    if (!Number.isFinite(price) || price <= 0) {
      toast.error('Veuillez saisir un prix valide.');
      return;
    }
    if (price >= 1000) {
      toast.error('PLU Carrefour ne fonctionne que pour les produits avec un prix < 1000 DH');
      return;
    }
    const plu = selectedPluCategory.plu;
    
    // Convert price to 6-digit format
    // First 3 digits: integer part (hundreds/units)
    // Last 3 digits: decimal part with trailing zero
    // Example: 526.21 => 526 + 210 = 526210
    const integerPart = Math.floor(price);
    const decimalPart = Math.round((price - integerPart) * 100);
    
    const integerStr = String(integerPart).padStart(3, '0');
    const decimalStr = String(decimalPart).padStart(2, '0') + '0';
    
    const priceStr = integerStr + decimalStr;
    // QR code format: PLU + 6-digit price
    const barcodeValue = plu + priceStr;

    // Generate QR code and open print window
    const productName = pluModalProduct?.name || '';
    try {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>PLU QR Code</title>
            <script src="https://cdn.jsdelivr.net/npm/qrcodejs@1.0.0/qrcode.min.js"></script>
            <style>
              body { margin: 0; padding: 0; }
              .plu-container {
                width: 48mm;
                max-width: 48mm;
                height: 23mm;
                max-height: 23mm;
                display: flex;
                align-items: center;
                border: 1px solid #000;
                box-sizing: border-box;
                page-break-inside: avoid;
              }
              .qr-section {
                flex: 0 0 23mm;
                height: 23mm;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 1mm;
                box-sizing: border-box;
              }
              #qrcode { display: inline-block; line-height: 0; }
              #qrcode img { 
                height: 21mm !important; 
                width: 21mm !important; 
                display: block;
              }
              .info-section {
                flex: 1;
                height: 23mm;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                padding: 1mm 2mm;
                box-sizing: border-box;
                text-align: center;
              }
              .price-label { 
                font-size: 14pt; 
                font-weight: bold; 
                margin: 0;
                line-height: 1.2;
              }
              .product-label { 
                font-size: 8pt; 
                margin: 2px 0 0 0;
                line-height: 1.1;
                word-wrap: break-word;
                overflow: hidden;
                max-height: 10mm;
              }
              @media print { 
                .no-print { display: none; }
                body { margin: 0; padding: 0; }
                .plu-container { border: 1px solid #000; }
              }
            </style>
          </head>
          <body>
            <div class="plu-container">
              <div class="qr-section">
                <div id="qrcode"></div>
              </div>
              <div class="info-section">
                <div class="price-label">${price.toFixed(2)} DHS</div>
                <div class="product-label">${productName}</div>
              </div>
            </div>
            <div class="no-print" style="margin-top: 20px; text-align: center;">
              <button onclick="window.print(); window.close();">Imprimer</button>
              <button onclick="window.close();">Fermer</button>
            </div>
            <script>
              window.onload = function() {
                new QRCode(document.getElementById("qrcode"), {
                  text: "${barcodeValue}",
                  width: 80,
                  height: 80,
                  colorDark: "#000000",
                  colorLight: "#ffffff",
                  correctLevel: QRCode.CorrectLevel.H
                });
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
      
      // Close the modal
      const modalElement = document.getElementById('pluCarrefourModal');
      if (modalElement) {
        const modal = window.bootstrap.Modal.getInstance(modalElement);
        if (modal) modal.hide();
      }
      
      closePluModal();
      toast.success('QR Code PLU généré avec succès');
    } catch (error) {
      console.error('Failed to generate PLU QR code', error);
      toast.error('Erreur lors de la génération du QR Code PLU');
    }
  };

  const getProductPlateformIds = (product) => {
    const plateformEntries = Array.isArray(product?.plateforms) ? product.plateforms : [];
    return new Set(
      plateformEntries
        .map((entry) => normalizeId(entry?.plateform_id || entry?._id || entry?.id || entry?.value))
        .filter(Boolean)
    );
  };

  const getProductKooulPlateformIds = (product) => {
    const plateformIds = new Set(getProductPlateformIds(product));

    if (product?.kooul?.plateform_id) {
      plateformIds.add(normalizeId(product.kooul.plateform_id));
    }
    if (product?.kooul?.plateformId) {
      plateformIds.add(normalizeId(product.kooul.plateformId));
    }

    return plateformIds;
  };

  const buildStoreAvailabilityCards = (productPlateforms = [], storesList = [], categories = []) => {
    const rawEntries = [];
   
    (Array.isArray(productPlateforms) ? productPlateforms : []).forEach((plateform) => {
      if (Array.isArray(plateform.roadmap)) {
        const parentPlateformId = normalizeId(
          plateform._id || plateform.id || plateform.plateform_id || plateform.value
        );
       
      
        plateform.roadmap.forEach((rm) => {
          const remoteId = rm.remote_id || rm.remoteId || rm.data?.id || rm.data?.remote_id || "";
          const roadmapType = rm.type || (rm.data && rm.data.type) || "N/A";
          const roadmapTypeLower = String(roadmapType).toLowerCase();
          const vendorIdCandidate =
            rm.vendorId || rm.vendor_id || rm.data?.vendorId || rm.data?.vendor_id || "";
          const storeIdent =
            rm.store_id || vendorIdCandidate || (rm.data && (rm.data.store_id || rm.data.vendorId)) || "";
          let updatedAt = null;
          if (rm.updatedAt) {
            if (typeof rm.updatedAt === "string") updatedAt = new Date(rm.updatedAt);
            else if (rm.updatedAt.$date) updatedAt = new Date(rm.updatedAt.$date);
            else updatedAt = new Date(rm.updatedAt);
          }

          const hasRemote = !!remoteId;
          const hasStore = !!storeIdent;
          if (!hasRemote && !hasStore) return;

          rawEntries.push({
            rm,
            remoteId,
            roadmapType,
            roadmapTypeLower,
            vendorIdCandidate,
            storeIdent,
            updatedAt,
            parentPlateformId:plateform.plateform_id || plateform._id || plateform.id || plateform.value,
          });
        });
      }

      
    });

    const kept = new Map();
    rawEntries.forEach((entry) => {
      const { remoteId, roadmapTypeLower, vendorIdCandidate, storeIdent, updatedAt } = entry;

      if (roadmapTypeLower === "glovo") {
        if (!vendorIdCandidate) return;
        const key = `${remoteId}-${vendorIdCandidate}-${roadmapTypeLower}`;
        const existing = kept.get(key);
        if (!existing) {
          kept.set(key, entry);
        } else {
          const existingDate = existing.updatedAt ? +existing.updatedAt : 0;
          const thisDate = updatedAt ? +updatedAt : 0;
          if (thisDate >= existingDate) {
            kept.set(key, entry);
          }
        }
      } else {
        const key = `${storeIdent}-${remoteId}-${entry.roadmapType}`;
        if (!kept.has(key)) {
          kept.set(key, entry);
        }
      }
    });

    const roadmapEntries = Array.from(kept.values()).map((entry) => {
      const r = entry.rm;
      if (!r.vendorId && entry.vendorIdCandidate) r.vendorId = entry.vendorIdCandidate;
      if (!r.store_id && entry.storeIdent) r.store_id = entry.storeIdent;
      try {
        r._parentPlateformId =
          entry.parentPlateformId || entry.parentPlateformId === 0 ? entry.parentPlateformId : entry.parentPlateformId;
      } catch (err) {}
      if (!r._parentPlateformId && entry.parentPlateformId) r._parentPlateformId = entry.parentPlateformId;
      // Preserve raw platform id on the roadmap for later resolution
      try {
        if (typeof entry.parentPlateformId !== 'undefined' && entry.parentPlateformId !== null) {
          r.plateform_id_raw = entry.parentPlateformId;
        }
      } catch (err) {}
      return r;
    });

    return roadmapEntries.map((roadmap) => {
     
      const storeIdCandidate =
        roadmap.store_id ||
        roadmap.vendorId ||
        (roadmap.data && (roadmap.data.store_id || roadmap.data.vendorId || roadmap.data.vendorIdCandidate));
      const storeIdNorm = normalizeId(storeIdCandidate);
      let mappingLocalId = null;
      let storeInfo =
        (Array.isArray(storesList) ? storesList : []).find((store) => {
          const candidates = [
            normalizeId(store._id),
            normalizeId(store.value),
            normalizeId(store.id),
            normalizeId(store.rawId),
            normalizeId(store.storeCode),
            normalizeId(store.code),
          ];
          return candidates.some((value) => value && storeIdNorm && String(value) === String(storeIdNorm));
        }) || null;

      let platformFound = null;
      const parentPlatform = (() => {
        if (!roadmap._parentPlateformId || !Array.isArray(glovoPlateforms)) return null;
        const parentId = normalizeId(roadmap._parentPlateformId);
        if (!parentId) return null;
        return (
          glovoPlateforms.find(
            (plateform) => normalizeId(plateform._id || plateform.id || plateform.plateform_id || plateform.value) === parentId
          ) || null
        );
      })();
    
      if (parentPlatform) {
        platformFound = parentPlatform;
      }

      if (!storeInfo && roadmap._parentPlateformId) {
        try {
          const parentId = normalizeId(roadmap._parentPlateformId);
          if (parentId && Array.isArray(glovoPlateforms)) {
            const fullPf = glovoPlateforms.find(
              (plateform) => normalizeId(plateform._id || plateform.id || plateform.plateform_id || plateform.value) === parentId
            );
            if (fullPf) {
              platformFound = platformFound || fullPf;
              const pfType = (
                fullPf.type ||
                fullPf.additionnal_data?.type ||
                fullPf.additional_data?.type ||
                ""
              )
                .toString()
                .toLowerCase();
          
            }
            else {
              // Fallback: try to resolve a platform using the product-level plateforms entries
              try {
                if (Array.isArray(productPlateforms)) {
                  const productLevelEntry = productPlateforms.find((entry) => {
                    const eid = normalizeId(entry?.plateform_id || entry?._id || entry?.id || entry?.value);
                    return eid && String(eid) === String(parentId);
                  });
                  if (productLevelEntry) {
                    
                    // try to find a full platform doc by the product-level entry's plateform_id
                    const targetPid = normalizeId(productLevelEntry?.plateform_id || productLevelEntry?._id || productLevelEntry?.id || productLevelEntry?.value);
                    const resolvedFullPf = (Array.isArray(glovoPlateforms) ? glovoPlateforms : []).find(
                      (p) => normalizeId(p._id || p.id || p.plateform_id || p.value) === targetPid
                    );
                    if (resolvedFullPf) {
                      
                      platformFound = platformFound || resolvedFullPf;
                    }

                    // If the product-level entry contains additionnal_data mapping, prefer it
                    const add = productLevelEntry.additionnal_data || productLevelEntry.additional_data || {};
                    const portal = add.portal || add.Portal || null;
                    const maps = portal?.stores_maped || portal?.stores_mapped || portal?.storesMapped || add.stores_maped || add.stores_mapped || null;
                    if (maps) {
                      const remoteVendorId = normalizeId(
                        roadmap.vendorId || roadmap.vendorIdCandidate || roadmap.data?.vendorId || roadmap.data?.id || roadmap.remote_id
                      );
                      let match = null;
                      if (Array.isArray(maps)) {
                        match = maps.find((entry) => normalizeId(entry.remote_store_id || entry.remoteStoreId || entry.remote_id || entry.remoteId || entry.remote) === remoteVendorId) || null;
                      } else if (maps && typeof maps === 'object') {
                        // If maps is object, it may map localId -> remoteId or include store_id keys
                        if (maps.store_id && (normalizeId(maps.remote_store_id) === remoteVendorId || normalizeId(maps.remoteStoreId) === remoteVendorId)) {
                          match = { store_id: maps.store_id, remote_store_id: maps.remote_store_id || maps.remoteStoreId };
                        } else {
                          const foundKey = Object.keys(maps).find((key) => normalizeId(maps[key]) === remoteVendorId);
                          if (foundKey) match = { store_id: foundKey, remote_store_id: maps[foundKey] };
                        }
                      }

                      if (match && match.store_id) {
                        const localId = match.store_id;
                        mappingLocalId = mappingLocalId || localId;
                        const localIdNorm = normalizeId(localId);
                        const foundStore = (Array.isArray(storesList) ? storesList : []).find((store) => {
                          const candidates = [normalizeId(store._id), normalizeId(store.value), normalizeId(store.id), normalizeId(store.rawId), normalizeId(store.storeCode), normalizeId(store.code)];
                          return candidates.some((value) => value && localIdNorm && String(value) === String(localIdNorm));
                        }) || null;
                        if (foundStore) {
                          storeInfo = foundStore;
                          platformFound = platformFound || resolvedFullPf || null;
                        }
                      }
                    }
                  }
                }
              } catch (err) {
                console.warn('[ProductTable] fallback product-level platform resolution error', err);
              }
            }
          }
        } catch (err) {}
      }

      if (!storeInfo && Array.isArray(productPlateforms)) {
        const remoteVendorId = normalizeId(roadmap.vendorId || roadmap.vendorIdCandidate || roadmap.data?.vendorId || roadmap.data?.id || roadmap.remote_id);
        for (const pf of productPlateforms) {
          try {
            const add = pf.additionnal_data || pf.additional_data || {};
            const maps = add.stores_maped || add.stores_mapped || add.storesMapped || null;
            if (!maps) continue;

            if (Array.isArray(maps)) {
              const found = maps.find((entry) => {
                const remote = normalizeId(entry.remote_store_id || entry.remoteStoreId || entry.remote_id || entry.remoteId || entry.remote);
                return remote && remoteVendorId && String(remote) === String(remoteVendorId);
              });
              if (found) {
                const localId = found.store_id || found.local_store_id || found.storeId || found.localId;
                if (localId) {
                  mappingLocalId = mappingLocalId || localId;
                  const localIdNorm = normalizeId(localId);
                  storeInfo =
                    (Array.isArray(storesList) ? storesList : []).find((store) => {
                      const candidates = [normalizeId(store._id), normalizeId(store.value), normalizeId(store.id), normalizeId(store.rawId), normalizeId(store.storeCode), normalizeId(store.code)];
                      return candidates.some((value) => value && localIdNorm && String(value) === String(localIdNorm));
                    }) || storeInfo;
                  if (storeInfo) {
                    platformFound = pf;
                    break;
                  }
                }
              }
            } else if (typeof maps === "object") {
              // maps object may map localId -> remoteId or contain explicit store_id keys
              let entryKey = null;
              if (maps.store_id && (normalizeId(maps.remote_store_id) === remoteVendorId || normalizeId(maps.remoteStoreId) === remoteVendorId)) {
                entryKey = maps.store_id;
              } else {
                entryKey = Object.keys(maps).find((key) => normalizeId(maps[key]) === remoteVendorId) || null;
              }
              if (entryKey) {
                const localId = entryKey;
                mappingLocalId = mappingLocalId || localId;
                const localIdNorm = normalizeId(localId);
                storeInfo =
                  (Array.isArray(storesList) ? storesList : []).find((store) => {
                    const candidates = [normalizeId(store._id), normalizeId(store.value), normalizeId(store.id), normalizeId(store.rawId), normalizeId(store.storeCode), normalizeId(store.code)];
                    return candidates.some((value) => value && localIdNorm && String(value) === String(localIdNorm));
                  }) || storeInfo;
                if (storeInfo) {
                  platformFound = pf;
                  break;
                }
              }
            }
          } catch (err) {
            // ignore per-entry errors
          }
        }
      }

      const roadmapType = roadmap.type || (roadmap.data && roadmap.data.type) || "N/A";
      // Determine if this roadmap references a glovo_sisters platform by checking the roadmap's
      // platform id (falling back to raw and parent ids) against the known glovoPlateforms list.
      const roadmapPlateformId = normalizeId(roadmap.plateform_id || roadmap.plateform_id_raw || roadmap._parentPlateformId || roadmap._parentPlateformId);
      const pfSis = Boolean(
        (Array.isArray(glovoPlateforms) ? glovoPlateforms : []).find(
          (p) => normalizeId(p._id || p.id || p.plateform_id || p.value) === roadmapPlateformId
        )?.type?.toString().toLowerCase() === 'glovo_sisters'
      );

      const platformDisplayName = (() => {
        if (pfSis) return 'Glovo Sis';
        
        if (platformFound) {
          const pfType = (
            platformFound.type ||
            platformFound.additionnal_data?.type ||
            platformFound.additional_data?.type ||
            ""
          )
            .toString()
            .toLowerCase();
          if (pfType === "glovo") return "glovo";
          return platformFound.name || platformFound.plateform_name || platformFound.type || roadmapType;
        }
        if (String(roadmapType || "").toLowerCase() === "glovo") return "glovo";

        return roadmapType;
      })();

      let storeName = "Inconnu";
      if (platformFound) {
        const pfType = (
          platformFound.type ||
          platformFound.additionnal_data?.type ||
          platformFound.additional_data?.type ||
          ""
        )
          .toString()
          .toLowerCase();
        if (pfSis) {
          storeName = "Glovo Sis";
        } else {
          storeName =
            storeInfo?.label ||
            storeInfo?.storeName ||
            storeInfo?.name ||
            (mappingLocalId ? `Mapped store ${mappingLocalId}` : storeIdCandidate ? `Vendor ${storeIdCandidate}` : "Inconnu");
        }
      } else {
        storeName =
          storeInfo?.label ||
          storeInfo?.storeName ||
          storeInfo?.name ||
          (mappingLocalId ? `Mapped store ${mappingLocalId}` : storeIdCandidate ? `Vendor ${storeIdCandidate}` : "Inconnu");
      }

      const isAvailable = (() => {
        const data = roadmap.data || {};
        const typeLower = String(roadmapType || "").toLowerCase();
        if (typeLower === "kooul") {
          if (typeof data.is_enabled !== "undefined") return data.is_enabled === 1 || data.is_enabled === true;
          if (data.availability && typeof data.availability.available !== "undefined") return !!data.availability.available;
          if (typeof roadmap.available !== "undefined") return !!roadmap.available;
          return false;
        }
        if (data.availability && typeof data.availability.available !== "undefined") return !!data.availability.available;
        if (typeof roadmap.available !== "undefined") return !!roadmap.available;
        return false;
      })();

      const parentCatRemoteId = normalizeId(roadmap.data?.parent_category_id);
      const matchedPc = (Array.isArray(categories) ? categories : []).find((pc) => {
        if (!pc) return false;
        if (String(pc.type || "").toLowerCase() !== "kooul") return false;
        const pcRemote = normalizeId(pc.remote_plateform_id || pc.remotePlateformId || pc.remote_id || pc.value || pc._id);
        return pcRemote && parentCatRemoteId && String(pcRemote) === String(parentCatRemoteId);
      });
      const categoryName =
        matchedPc ? matchedPc.name || matchedPc.label : (roadmap.data && (roadmap.data.category?.name || roadmap.data.category)) || "Sans catégorie";

      const roadmapTypeLower = String(roadmapType || "").toLowerCase();
      const borderStyle =
        roadmapTypeLower === "kooul" ? "2px solid #dc3545" : roadmapTypeLower === "glovo" ? "2px solid #ffc107" : "none";

      return {
        roadmap,
        storeName,
        platformDisplayName,
        categoryName,
        isAvailable,
        borderStyle,
        storeInfo,
        roadmapType,
        parentPlateformId: roadmap._parentPlateformId,
      };
    });
  };

  const getKooulButtonDisabled = (product) => {
    if (!kooulPlateformIds.length) {
      return true;
    }
    const productKooulIds = getProductKooulPlateformIds(product);
    const hasMissingPlatform = kooulPlateformIds.some((plateformId) => !productKooulIds.has(plateformId));
    return !hasMissingPlatform;
  };

  const resolveComponentId = (componentEntry) => {
    if (!componentEntry) return '';

    const reference =
      typeof componentEntry === 'object' && (componentEntry.product || componentEntry.component)
        ? componentEntry.product ?? componentEntry.component
        : componentEntry;

    if (!reference) return '';
    if (typeof reference === 'string') return reference;
    if (typeof reference === 'object') {
      return (
        reference._id ||
        reference.id ||
        reference.value ||
        reference.product ||
        reference.component ||
        ''
      );
    }
    return '';
  };

  const matchesComponentFilter = (product) => {
    if (!componentFilter.length) {
      return true;
    }
    if (!Array.isArray(product?.components)) {
      return false;
    }
    return componentFilter.every((filterId) =>
      product.components.some((pc) => resolveComponentId(pc) === filterId)
    );
  };

  const matchesPlateformFilter = (product) => {
    if (!plateformFilters || plateformFilters.length === 0) {
      return true;
    }

    const entries = Array.isArray(product?.plateforms) ? product.plateforms : [];
    if (!entries.length) {
      return false;
    }

    const productPlateformIds = entries
      .map((entry) => normalizeId(entry?.plateform_id || entry?._id || entry?.id || entry?.value))
      .filter(Boolean);

    if (!productPlateformIds.length) {
      return false;
    }

    return plateformFilters.some((filterId) => productPlateformIds.includes(filterId));
  };

  const isValidEAN13 = (code) => {

    if (code === null || code === undefined) return false;
    const codeStr = String(code);
    if (!/^\d{13}$/.test(codeStr)) return false;
    const digits = codeStr.split('').map(Number);

    const checkDigit = digits.pop();
    const sum = digits.reduce((acc, n, idx) => acc + n * (idx % 2 === 0 ? 1 : 3), 0);
    const calculated = (10 - (sum % 10)) % 10;
    return checkDigit === calculated;
  };

  const resolveImageUrl = (path) => {
    if (!path) return null;

    const normalized = `${path}`.trim();
    if (!normalized) return null;

    if (/^(https?:)?\/\//i.test(normalized) || /^(data|blob):/i.test(normalized)) {
      return normalized;
    }

    return `${OFFICE_IMAGE_BASE_URL.replace(/\/$/, "")}/${normalized.replace(/^\//, "")}`;
  };

  const openImageModal = (product) => {
    const mainImage = resolveImageUrl(product?.url_product);
    const galleryImages = Array.isArray(product?.gallery_images)
      ? product.gallery_images
          .map((image) => resolveImageUrl(image))
          .filter((image) => Boolean(image))
      : [];

    const allImages = [mainImage, ...galleryImages].filter((image) => Boolean(image));
    setModalImages(allImages);
    setModalImage(allImages[0] || null);
  };

  useEffect(() => {
    // Iterate over products and generate barcode for each one
    products.forEach((product) => {
      const barcodeElement = barcodeRefs.current[product._id];
      if (
        barcodeElement &&
        product.barcode &&
        product.type !== 'dolibarr' &&
        isValidEAN13(product.barcode)
      ) {

        JsBarcode(barcodeElement, String(product.barcode), {
          format: "EAN13",
          lineColor: "#0b0b0b",
          width: 1,
          height: 50,
          displayValue: true,
        });
      }
    });
  }, [products, currentPage]);

  useEffect(() => {
    const fetchVariables = async () => {
      try {
        const data = await VariableService.listVariables('prompts');
        setVariables(data);
      } catch (err) {
        console.error('Error fetching variables', err);
      }
    };
    fetchVariables();
  }, []);
  const variableOptions = useMemo(
    () => variables.map(v => ({ value: v._id || v.id, label: v.name })),
    [variables]
  );
  const selectedVariable = useMemo(
    () => variables.find((entry) => selectedVariableIds.includes(entry._id || entry.id)) || null,
    [variables, selectedVariableIds]
  );
  const selectedVariableOption = useMemo(
    () => variableOptions.find((option) => selectedVariableIds.includes(option.value)) || null,
    [variableOptions, selectedVariableIds]
  );
  const promptVariableDefinitions = useMemo(
    () =>
      Array.isArray(selectedVariable?.prompt_variables_definition) &&
      selectedVariable.prompt_variables_definition.length
        ? selectedVariable.prompt_variables_definition.map((entry) => ({
            key: String(entry?.key || "").trim(),
            label: String(entry?.label || entry?.key || "").trim(),
            type: String(entry?.type || "string").trim(),
            default_value: String(entry?.default_value || "").trim(),
            value: "",
          }))
        : parsePromptVariableDefinitions(selectedVariable?.prompt_template || selectedVariable?.value),
    [selectedVariable]
  );
  const promptVariables = useMemo(() => promptVariableDefinitions, [promptVariableDefinitions]);
  const draftCategoryIds = [
    "67a3946b2df98d164e4071cd",
    "67a3946b2df98d164e4071ce",
    "67a3946b2df98d164e4071cf",
    "67ddd462e3c9436483873453",
    "67ddd4dde3c9436483873457",
    "67f41375980623c125755b87",
    "67f4139b980623c125755b88",
    "67ddd510e3c9436483873458"
  ];
  const searchSource = searchTerm && fullProducts.length ? fullProducts : products;
  const filteredProducts = (searchSource?.filter(product =>
    product?.name && product.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (
      dynfilter.length === 0 ||
      dynfilter.some(filter => {
        if (filter === "draft") {
          return !product?.marjane || product?.marjane.status==="draft" || !product?.marjane.status;
        }
        return product.marjane?.status === filter;
      })
    ) &&
    (
      !selectedCategory || product.category === selectedCategory.value
    ) &&
    matchesPlateformFilter(product) &&
    matchesComponentFilter(product)
  ) || []).sort(compareProductCreationDescending);


  // Pagination logic
  const shouldUseServerPagination = serverPagination && !searchTerm;
  const totalPages = shouldUseServerPagination
    ? Math.max(serverTotalPages || 1, 1)
    : Math.ceil((filteredProducts?.length || 1) / itemsPerPage);
  const displayedProducts = shouldUseServerPagination
    ? filteredProducts
    : filteredProducts?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const displayedWooProducts = useMemo(
    () =>
      (Array.isArray(displayedProducts) ? displayedProducts : [])
        .map((product) => ({
          product,
          localId: normalizeId(product?._id || product?.id),
          wooId: normalizeId(product?.woocommerce_id),
        }))
        .filter(({ localId, wooId }) => Boolean(localId && wooId)),
    [displayedProducts]
  );
  const displayedWooFetchKey = useMemo(
    () => displayedWooProducts.map(({ localId, wooId }) => `${localId}:${wooId}`).join('|'),
    [displayedWooProducts]
  );
  let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
  let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

  if (endPage - startPage < maxPagesToShow - 1) {
    startPage = Math.max(1, endPage - maxPagesToShow + 1);
  }
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const visibleProductIds = filteredProducts.map((product) => product._id);
      setSelectedProducts(visibleProductIds);
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectOne = (productId) => {
    setSelectedProducts((prevSelected) =>
      prevSelected.includes(productId)
        ? prevSelected.filter((id) => id !== productId)
        : [...prevSelected, productId]
    );
  };

  const getProductComputedState = (product) => {
    const productPlateformIds = getProductPlateformIds(product);
    const productPlateformEntries = Array.isArray(product?.plateforms) ? product.plateforms : [];
    const regularPrice = parsePrice(product?.regular_price);
    const salePrice = parsePrice(product?.sale_price ?? product?.discounted_price);
    const hasSalePrice = salePrice !== null && salePrice > 0;
    const hasWebsite = Boolean(product?.woocommerce_id);
    const productLocalId = normalizeId(product?._id || product?.id);
    const websiteStatus = wooStatusByProductId[productLocalId];
    const websiteLoading = Boolean(websiteStatus?.loading);
    const websiteStockStatus = websiteStatus?.stockStatus || null;
    const websiteInStock = websiteStockStatus === "instock";
    const websiteFeatured = Boolean(websiteStatus?.featured);
    const websiteRegularPrice = parsePrice(websiteStatus?.regularPrice ?? product?.regular_price);
    const websiteSalePrice = parsePrice(websiteStatus?.salePrice ?? product?.sale_price ?? product?.discounted_price);
    const websiteHasDiscount = websiteRegularPrice !== null && websiteSalePrice !== null && websiteSalePrice > 0 && websiteSalePrice < websiteRegularPrice;
    const productGlovoPlateformIds = getProductGlovoFamilyPlateformIds(product);
    const hasGlovo = glovoPlateformIds.size
      ? Array.from(productPlateformIds).some((plateformId) => glovoPlateformIds.has(plateformId))
      : productGlovoPlateformIds.size
        ? productGlovoPlateformIds.size > 0
        : Array.isArray(product?.glovo_id)
          ? product.glovo_id.length > 0
          : !!product?.glovo_id;
    const productKooulIds = getProductKooulPlateformIds(product);
    const hasKooulLink = kooulPlateformIds.some((plateformId) => productKooulIds.has(plateformId));
    const selectionActive = selectedProducts.includes(product._id);

    return {
      hasGlovo,
      hasKooulLink,
      hasSalePrice,
      hasWebsite,
      productLocalId,
      productPlateformEntries,
      productPlateformIds,
      regularPrice,
      salePrice,
      selectionActive,
      websiteFeatured,
      websiteHasDiscount,
      websiteInStock,
      websiteLoading,
      websiteRegularPrice,
      websiteSalePrice,
      websiteStatus,
      websiteStockStatus,
    };
  };

  const getWebsiteStatusLabel = (websiteStatus, websiteLoading, websiteStockStatus) => {
    if (websiteLoading) return "Verification du statut...";
    if (websiteStockStatus === "instock") return "En stock";
    if (websiteStockStatus === "outofstock") return "Rupture de stock";
    if (websiteStatus?.error) return "Statut distant indisponible";
    return "Statut inconnu";
  };

  useEffect(() => {
    if (!displayedWooProducts.length) {
      return;
    }

    const productsNeedingFetch = displayedWooProducts.filter(({ localId, wooId }) => {
      const cached = wooStatusByProductId[localId];
      return !cached || String(cached.wooId) !== String(wooId);
    });

    if (!productsNeedingFetch.length) {
      return;
    }

    setWooStatusByProductId((prev) => {
      const next = { ...prev };
      productsNeedingFetch.forEach(({ localId, wooId }) => {
        next[localId] = {
          ...(next[localId] || {}),
          wooId,
          loading: true,
          error: false,
        };
      });
      return next;
    });

    const fetchStatuses = async () => {
      try {
        const remoteProducts = await getWooCommerceProductsByIds(
          productsNeedingFetch.map(({ wooId }) => wooId)
        );
        const remoteByWooId = new Map(
          (Array.isArray(remoteProducts) ? remoteProducts : []).map((remoteProduct) => [
            String(remoteProduct?.id),
            remoteProduct,
          ])
        );

        setWooStatusByProductId((prev) => {
          const next = { ...prev };
          productsNeedingFetch.forEach(({ localId, wooId }) => {
            const remoteProduct = remoteByWooId.get(String(wooId)) || null;
            next[localId] = {
              wooId,
              loading: false,
              fetched: true,
              error: !remoteProduct,
              stockStatus: remoteProduct?.stock_status || null,
              status: remoteProduct?.status || null,
              featured: Boolean(remoteProduct?.featured),
              regularPrice: remoteProduct?.regular_price || remoteProduct?.price || "",
              salePrice: remoteProduct?.sale_price || "",
              pegasusAddons: readPegasusAddonsMeta(remoteProduct),
            };
          });
          return next;
        });
      } catch (error) {
        setWooStatusByProductId((prev) => {
          const next = { ...prev };
          productsNeedingFetch.forEach(({ localId, wooId }) => {
            next[localId] = {
              ...(next[localId] || {}),
              wooId,
              loading: false,
              fetched: true,
              error: true,
            };
          });
          return next;
        });
      }
    };

    fetchStatuses();
  }, [displayedWooFetchKey]);

  const handleWooAvailabilityToggle = async (product) => {
    const localId = normalizeId(product?._id || product?.id);
    if (!localId || !product?.woocommerce_id) {
      return;
    }

    const cachedStatus = wooStatusByProductId[localId];
    const currentStockStatus = cachedStatus?.stockStatus;

    const nextInStock = currentStockStatus !== "instock";

    setWooStatusByProductId((prev) => ({
      ...prev,
      [localId]: {
        ...(prev[localId] || {}),
        wooId: product.woocommerce_id,
        loading: true,
        error: false,
      },
    }));

    try {
      const updatedProduct = await updateWooCommerceProductStockStatus(product.woocommerce_id, nextInStock);
      setWooStatusByProductId((prev) => ({
        ...prev,
        [localId]: {
          ...(prev[localId] || {}),
          wooId: product.woocommerce_id,
          loading: false,
          fetched: true,
          error: false,
          stockStatus: updatedProduct?.stock_status || (nextInStock ? "instock" : "outofstock"),
          status: updatedProduct?.status || prev[localId]?.status || null,
        },
      }));
      toast.success(`Site web ${nextInStock ? "en stock" : "en rupture"} pour ${product.name}`);
    } catch (error) {
      setWooStatusByProductId((prev) => ({
        ...prev,
        [localId]: {
          ...(prev[localId] || {}),
          wooId: product.woocommerce_id,
          loading: false,
          error: true,
        },
      }));
      toast.error("Erreur lors de la mise à jour WooCommerce.");
    }
  };

  const handleWooFeaturedToggle = async (product) => {
    const localId = normalizeId(product?._id || product?.id);
    if (!localId || !product?.woocommerce_id) {
      return;
    }

    const cachedStatus = wooStatusByProductId[localId];
    const nextFeatured = !Boolean(cachedStatus?.featured);

    setWooStatusByProductId((prev) => ({
      ...prev,
      [localId]: {
        ...(prev[localId] || {}),
        wooId: product.woocommerce_id,
        loading: true,
        error: false,
      },
    }));

    try {
      const updatedProduct = await updateWooCommerceProductFeaturedStatus(product.woocommerce_id, nextFeatured);
      setWooStatusByProductId((prev) => ({
        ...prev,
        [localId]: {
          ...(prev[localId] || {}),
          wooId: product.woocommerce_id,
          loading: false,
          fetched: true,
          error: false,
          stockStatus: updatedProduct?.stock_status || prev[localId]?.stockStatus || null,
          status: updatedProduct?.status || prev[localId]?.status || null,
          featured: Boolean(updatedProduct?.featured),
        },
      }));
      toast.success(`Produit ${nextFeatured ? "mis en avant" : "retiré de la mise en avant"} sur le site`);
    } catch (error) {
      setWooStatusByProductId((prev) => ({
        ...prev,
        [localId]: {
          ...(prev[localId] || {}),
          wooId: product.woocommerce_id,
          loading: false,
          error: true,
        },
      }));
      toast.error("Erreur lors de la mise en avant WooCommerce.");
    }
  };

  const openWebsiteDiscountModal = (product) => {
    const localId = normalizeId(product?._id || product?.id);
    const websiteStatus = localId ? wooStatusByProductId[localId] : null;
    const regular = parsePrice(websiteStatus?.regularPrice ?? product?.regular_price);
    const sale = parsePrice(websiteStatus?.salePrice ?? product?.sale_price ?? product?.discounted_price);
    const regularValue = regular !== null ? regular.toFixed(2) : "";
    const saleValue = sale !== null && sale > 0 ? sale.toFixed(2) : "";

    setWebsiteDiscountProduct(product);
    setWebsiteDiscountForm({
      regularPrice: regularValue,
      percentage: calculateDiscountPercent(regularValue, saleValue),
      salePrice: saleValue,
    });
  };

  const openWebsiteAddonsModal = (product) => {
    const localId = normalizeId(product?._id || product?.id);
    const websiteStatus = localId ? wooStatusByProductId[localId] : null;
    const assigned = Array.isArray(websiteStatus?.pegasusAddons) ? websiteStatus.pegasusAddons : [];
    setWebsiteAddonsProduct(product);
    setSelectedWebsiteAddonIds(
      assigned.map((addon) => normalizeId(addon.id || addon._id)).filter(Boolean)
    );
  };

  const saveWebsiteAddons = async () => {
    if (!websiteAddonsProduct?.woocommerce_id) {
      toast.error("Produit WooCommerce introuvable.");
      return;
    }

    const selectedAddons = (Array.isArray(productAddons) ? productAddons : []).filter((addon) =>
      selectedWebsiteAddonIds.includes(normalizeId(addon?._id || addon?.id))
    );
    const localId = normalizeId(websiteAddonsProduct?._id || websiteAddonsProduct?.id);

    setWebsiteAddonsSaving(true);
    try {
      const updatedProduct = await updateWooCommerceProductAddonsMeta(
        websiteAddonsProduct.woocommerce_id,
        selectedAddons
      );
      setWooStatusByProductId((prev) => ({
        ...prev,
        [localId]: {
          ...(prev[localId] || {}),
          wooId: websiteAddonsProduct.woocommerce_id,
          loading: false,
          fetched: true,
          error: false,
          stockStatus: updatedProduct?.stock_status || prev[localId]?.stockStatus || null,
          status: updatedProduct?.status || prev[localId]?.status || null,
          featured: Boolean(updatedProduct?.featured ?? prev[localId]?.featured),
          regularPrice: updatedProduct?.regular_price || prev[localId]?.regularPrice || "",
          salePrice: updatedProduct?.sale_price || prev[localId]?.salePrice || "",
          pegasusAddons: readPegasusAddonsMeta(updatedProduct),
        },
      }));
      toast.success("Options site synchronisées.");
      closeBootstrapModal("websiteAddonsModal");
      setWebsiteAddonsProduct(null);
    } catch (error) {
      toast.error("Impossible de synchroniser les options site.");
    } finally {
      setWebsiteAddonsSaving(false);
    }
  };

  const handleWebsiteDiscountPercentageChange = (event) => {
    const percentage = event.target.value;
    setWebsiteDiscountForm((prev) => ({
      ...prev,
      percentage,
      salePrice: calculateSalePriceFromPercent(prev.regularPrice, percentage),
    }));
  };

  const handleWebsiteDiscountSalePriceChange = (event) => {
    const salePrice = event.target.value;
    setWebsiteDiscountForm((prev) => ({
      ...prev,
      salePrice,
      percentage: calculateDiscountPercent(prev.regularPrice, salePrice),
    }));
  };

  const saveWebsiteDiscount = async ({ clear = false } = {}) => {
    if (!websiteDiscountProduct?.woocommerce_id) {
      toast.error("Produit WooCommerce introuvable.");
      return;
    }

    const localId = normalizeId(websiteDiscountProduct?._id || websiteDiscountProduct?.id);
    const regular = parsePrice(websiteDiscountForm.regularPrice);
    const sale = clear ? null : parsePrice(websiteDiscountForm.salePrice);

    if (regular === null || regular <= 0) {
      toast.error("Prix avant remise invalide.");
      return;
    }

    if (!clear && (sale === null || sale <= 0 || sale >= regular)) {
      toast.error("Le prix après remise doit être inférieur au prix avant remise.");
      return;
    }

    setWebsiteDiscountSaving(true);
    try {
      const updatedProduct = await updateWooCommerceProductPricing(websiteDiscountProduct.woocommerce_id, {
        regular_price: formatPriceValue(regular),
        sale_price: clear ? "" : formatPriceValue(sale),
      });

      setWooStatusByProductId((prev) => ({
        ...prev,
        [localId]: {
          ...(prev[localId] || {}),
          wooId: websiteDiscountProduct.woocommerce_id,
          loading: false,
          fetched: true,
          error: false,
          stockStatus: updatedProduct?.stock_status || prev[localId]?.stockStatus || null,
          status: updatedProduct?.status || prev[localId]?.status || null,
          featured: Boolean(updatedProduct?.featured ?? prev[localId]?.featured),
          regularPrice: updatedProduct?.regular_price || formatPriceValue(regular),
          salePrice: updatedProduct?.sale_price || "",
        },
      }));

      toast.success(clear ? "Remise WooCommerce retirée." : "Remise WooCommerce appliquée.");
      closeBootstrapModal("websiteDiscountModal");
      setWebsiteDiscountProduct(null);
    } catch (error) {
      toast.error("Impossible d'appliquer la remise WooCommerce.");
    } finally {
      setWebsiteDiscountSaving(false);
    }
  };

  const handleIsComponentToggle = async (product, nextValue) => {
    if (typeof onToggleIsComponent !== 'function' || !product?._id) {
      return;
    }

    const productId = product._id;
    setComponentToggleLoading((prev) => ({ ...prev, [productId]: true }));
    try {
      await onToggleIsComponent(productId, nextValue);
    } finally {
      setComponentToggleLoading((prev) => ({ ...prev, [productId]: false }));
    }
  };

  const openProcessModal = (product) => {
    setProcessProduct(product);
    setSelectedVariableIds([]);
    setAdditionnalPrompt("");
    setImageProcessingType("chatgpt");
  };

  const handleVariableChange = (option) => {
    setSelectedVariableIds(option ? [option.value] : []);
  };

  const handlePromptVariableChange = (key, value) => {
    setProcessProduct((prev) => {
      if (!prev) return prev;

      const currentAdditionnalData = prev.additionnal_data || {};
      const currentEntries = Array.isArray(currentAdditionnalData.prompt_variables)
        ? currentAdditionnalData.prompt_variables
        : [];

      return {
        ...prev,
        additionnal_data: {
          ...currentAdditionnalData,
          prompt_variables: currentEntries.map((entry) =>
            entry.key === key ? { ...entry, value } : entry
          ),
        },
      };
    });
  };

  useEffect(() => {
    setProcessProduct((prev) => {
      if (!prev) return prev;

      const currentAdditionnalData = prev.additionnal_data || {};
      const currentEntries = Array.isArray(currentAdditionnalData.prompt_variables)
        ? currentAdditionnalData.prompt_variables
        : [];
      const nextEntries = normalizePromptVariableEntries(promptVariableDefinitions, currentEntries);

      if (arePromptVariablesEqual(currentEntries, nextEntries)) {
        return prev;
      }

      return {
        ...prev,
        additionnal_data: {
          ...currentAdditionnalData,
          prompt_variables: nextEntries,
        },
      };
    });
  }, [promptVariableDefinitions]);

  const blobToDataUrl = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result);
          return;
        }
        reject(new Error("Unable to convert blob to base64"));
      };
      reader.onerror = () => reject(reader.error || new Error("FileReader error"));
      reader.readAsDataURL(blob);
    });

  const compressImageToDataUrl = (blob, options = {}) =>
    new Promise((resolve, reject) => {
      const maxDimension = options.maxDimension || 1280;
      const quality = options.quality ?? 0.8;
      const outputType = options.outputType || "image/jpeg";
      const imageUrl = URL.createObjectURL(blob);
      const image = new Image();

      image.onload = () => {
        try {
          const width = image.naturalWidth || image.width;
          const height = image.naturalHeight || image.height;

          if (!width || !height) {
            throw new Error("Invalid image dimensions");
          }

          const ratio = Math.min(1, maxDimension / Math.max(width, height));
          const targetWidth = Math.max(1, Math.round(width * ratio));
          const targetHeight = Math.max(1, Math.round(height * ratio));
          const canvas = document.createElement("canvas");
          canvas.width = targetWidth;
          canvas.height = targetHeight;

          const context = canvas.getContext("2d");
          if (!context) {
            blobToDataUrl(blob).then(resolve).catch(reject);
            return;
          }

          context.drawImage(image, 0, 0, targetWidth, targetHeight);
          resolve(canvas.toDataURL(outputType, quality));
        } catch (error) {
          reject(error);
        } finally {
          URL.revokeObjectURL(imageUrl);
        }
      };

      image.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        reject(new Error("Unable to load image for compression"));
      };

      image.src = imageUrl;
    });

  const getImageProcessingId = (payload) =>
    payload?._id ||
    payload?.id ||
    payload?.image_processing?._id ||
    payload?.image_processing?.id ||
    payload?.data?._id ||
    payload?.data?.id;

  const handleProcessImage = async () => {
    if (!processProduct) return;

    try {
      // Build original URL
      const srcUrl = resolveImageUrl(processProduct.url_product);
      if (!srcUrl) {
        throw new Error("Image source missing");
      }

      // Send request to your PHP endpoint
      const proxyResponse = await fetch(`${OFFICE_IMAGE_BASE_URL.replace(/\/$/, "")}/serve-file.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ path: srcUrl }),
      });

      if (!proxyResponse.ok) {
        throw new Error(`Failed to fetch image: ${proxyResponse.status}`);
      }

      // Resize and compress before converting to base64 so the JSON payload stays smaller.
      const blob = await proxyResponse.blob();
      const srcBase64 = await compressImageToDataUrl(blob, {
        maxDimension: 1280,
        quality: 0.8,
        outputType: "image/jpeg",
      });

      const createdRecord = await ImageProcessingService.createImageProcessing({
        src: srcBase64,
        variable_ids: selectedVariableIds,
        product_id: processProduct._id,
        additionnal_prompt: additionnalPrompt,
        additionnal_data: {
          ...(processProduct?.additionnal_data || {}),
          ...(Array.isArray(processProduct?.additionnal_data?.prompt_variables) &&
          processProduct.additionnal_data.prompt_variables.length
            ? { prompt_variables: processProduct.additionnal_data.prompt_variables }
            : {}),
        },
        type: imageProcessingType || "chatgpt",
      });

      if (!getImageProcessingId(createdRecord)) {
        throw new Error("Image processing id missing from create response");
      }

    toast.success("Image envoyée pour traitement");
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'envoi de l'image");
    }
  };

  

  const isAllSelected = displayedProducts.every((product) =>
    selectedProducts.includes(product._id)
  );

  return (
    <div>
      <div className="d-flex flex-column flex-lg-row gap-3 align-items-stretch align-items-lg-center justify-content-between mb-3">
        <div className="flex-grow-1">
          <input
            type="text"
            placeholder="Search by name / WooCommerce..."
            className="form-control"
            value={searchTerm}
            onChange={(e) => {
              const newSearchTerm = e.target.value;
              setSearchTerm(newSearchTerm);
              setCurrentPage(1);

              if (newSearchTerm && fullProducts.length === 0 && onLoadAllProducts) {
                toast.info('Chargement de tous les produits pour la recherche...');
                onLoadAllProducts();
              }
            }}
          />
          <small className="text-muted">
            La recherche par nom charge tous les produits si besoin pour retrouver les produits lies a WooCommerce.
          </small>
        </div>
        <div className="btn-group" role="group" aria-label="Product view mode">
          <button
            type="button"
            className={`btn ${viewMode === "table" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setViewMode("table")}
          >
            <i className="fa-solid fa-table me-2"></i>
            Table
          </button>
          <button
            type="button"
            className={`btn ${viewMode === "cards" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setViewMode("cards")}
          >
            <i className="fa-solid fa-grip me-2"></i>
            Cards
          </button>
        </div>
      </div>

      {viewMode === "table" ? (
      <table className="table table-bordered table-responsive table-striped w-100">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                onChange={handleSelectAll}
                checked={isAllSelected && displayedProducts.length > 0}
              />
            </th>
            <th className="col-2">Bar code</th>
            <th>Image</th>
            <th>Name</th>

            <th>Composant</th>
            <th>Disponibilité</th>

            <th>Store Availability</th>
            
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="text-center py-4">
                  Chargement des produits...
                </td>
              </tr>
            ) : (
              displayedProducts.map((product) => {
              const {
                hasGlovo,
                hasKooulLink,
                hasSalePrice,
                hasWebsite,
                productLocalId,
                productPlateformEntries,
                productPlateformIds,
                regularPrice,
                salePrice,
                websiteFeatured,
                websiteHasDiscount,
                websiteInStock,
                websiteLoading,
                websiteRegularPrice,
                websiteSalePrice,
                websiteStatus,
                websiteStockStatus,
              } = getProductComputedState(product);
                  // Debug: log when a product references a platform whose full doc is of type `glovo_sisters`
                  try {
                    (productPlateformEntries || []).forEach(entry => {
                      const pid = normalizeId(entry?.plateform_id || entry?._id || entry?.id || entry?.value);
                      if (!pid) return;
                      const fullPf = (Array.isArray(glovoPlateforms) ? glovoPlateforms : []).find(p => normalizeId(p._id || p.id || p.plateform_id || p.value) === pid) || null;
                      if (fullPf && String((fullPf.type || fullPf.additionnal_data?.type || fullPf.additional_data?.type || '').toLowerCase()) === 'glovo_sisters') {
                       
                      }
                    });
                  } catch (err) {
                    console.warn('[ProductTable] diagnostic check error', err);
                  }
            return (
              <tr key={product._id}>
              <td>
                <input
                  type="checkbox"
                  checked={selectedProducts.includes(product._id)}
                  onChange={() => handleSelectOne(product._id)}
                />
              </td>
              <td width="100px">
                {product.type === 'dolibarr' || !isValidEAN13(product.barcode) ? (
                  <span>{product.barcode}</span>
                ) : (
                  <svg
                    ref={(el) => {
                      if (el) {
                        barcodeRefs.current[product._id] = el; // Assign ref to each barcode SVG
                      }
                    }}
                    width={100}
                  />
                )}

              </td>
              <td>
                <div className="d-flex flex-column align-items-start">
                  <img
                    src={resolveImageUrl(product.url_product) || ""}
                    width={100}
                    style={{ cursor: "pointer" }}
                    data-bs-toggle="modal"
                    data-bs-target="#imageModal"
                    onClick={() => openImageModal(product)}
                    alt={product.name}
                  />
                  {Array.isArray(product?.gallery_images) && product.gallery_images.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary mt-2"
                      data-bs-toggle="modal"
                      data-bs-target="#imageModal"
                      onClick={() => openImageModal(product)}
                    >
                      +{product.gallery_images.length} more
                    </button>
                  )}
                </div>
              </td>
              <td>
                <div className="d-flex flex-column align-items-start gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <div className="d-flex align-items-center gap-1">
                      {hasGlovo && (
                        <img
                          src={GLOVO_LOGO_URL}
                          alt="Glovo"
                          style={{ width: 22, height: 22, borderRadius: "50%", objectFit: "cover" }}
                        />
                      )}
                      {hasKooulLink && (
                        <img
                          src={KOUL_LOGO_URL}
                          alt="Kooul"
                          style={{ width: 22, height: 22, borderRadius: "50%", objectFit: "cover" }}
                        />
                      )}
                      {hasWebsite && (
                        <span title="Publie sur site web" style={{ color: "#0ea5e9", display: "inline-flex", alignItems: "center" }}>
                          <i className="fa-solid fa-globe"></i>
                        </span>
                      )}
                    </div>
                    <span>{product.name}</span>
                    {renderWebsiteDeliveryBadges(product)}
                  </div>
                  <div className="d-flex flex-column align-items-start gap-1">
                    {regularPrice !== null && (
                      <span className={`badge ${hasSalePrice ? "bg-secondary" : "bg-primary"}`}>
                        Regular: {formatPrice(regularPrice)}
                      </span>
                    )}
                    {hasSalePrice && (
                      <span className="badge bg-success">
                        Sale: {formatPrice(salePrice)}
                      </span>
                    )}
                    {hasWebsite && (
                      <span className="badge bg-info text-dark">
                        Déjà publié sur site web
                      </span>
                    )}
                  </div>
                </div>
              </td>

              <td>
                <button
                  type="button"
                  className={`btn btn-sm ${product.isComposant ? "btn-warning" : "btn-outline-secondary"}`}
                  onClick={() => handleIsComponentToggle(product, !Boolean(product.isComposant))}
                  disabled={componentToggleLoading[product._id]}
                  style={{ minWidth: "110px" }}
                >
                  <i className={`fa-solid ${product.isComposant ? "fa-toggle-on" : "fa-toggle-off"} me-2`}></i>
                  {product.isComposant ? "Activé" : "Désactivé"}
                </button>
              </td>

              <td>
                <button
                  type="button"
                  className={`btn btn-sm ${product.availability ? "btn-success" : "btn-outline-danger"}`}
                  onClick={() => updateGlobalAvailability(product._id, !product.availability)}
                  style={{ minWidth: "120px" }}
                >
                  <i className={`fa-solid ${product.availability ? "fa-toggle-on" : "fa-toggle-off"} me-2`}></i>
                  {product.availability ? "Disponible" : "Indisponible"}
                </button>
              </td>

              <td>
                {renderVaseSelector(product)}
                {(() => {
                  const cards = buildStoreAvailabilityCards(
                    product?.plateforms,
                    stores,
                    productCategories
                  );
                  const websiteCard = hasWebsite ? (
                    (() => {
                      const deliveryDraft = getWebsiteDeliveryDraft(product);
                      const deliveryChanged = !areWebsiteDeliveriesEqual(deliveryDraft, product?.website_delivery);
                      const deliveryKey = websiteDeliveryKey(product);
                      const selectedZones = deliveryDraft.express_zones.map((zone) => ({
                        value: zone.code,
                        label: zone.name || zone.code,
                      }));
                      const websiteAddonsCount = Array.isArray(websiteStatus?.pegasusAddons)
                        ? websiteStatus.pegasusAddons.length
                        : 0;

                      return (
                    <div
                      key={`website-${product._id}`}
                      className={`badge text-bg-${websiteLoading ? "warning" : websiteInStock ? "success" : "secondary"} mx-1 mb-1`}
                      style={{
                        fontSize: '0.75rem',
                        padding: '0.5em 0.65em',
                        display: 'inline-flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        maxWidth: '220px',
                        minWidth: '180px',
                        border: '1px solid rgba(0,0,0,0.12)'
                      }}
                    >
                      <div style={{
                        fontWeight: '700',
                        marginBottom: '4px',
                        width: '100%',
                        borderBottom: '1px solid rgba(255,255,255,0.3)',
                        paddingBottom: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <span>
                          <i className="fas fa-globe" style={{ fontSize: '0.7em', marginRight: '4px' }}></i>
                          Site Web
                        </span>
                        <span className="badge bg-light text-dark" style={{ fontSize: '0.7em', padding: '2px 5px' }}>
                          WooCommerce
                        </span>
                      </div>
                      <div style={{ marginBottom: '4px', fontSize: '0.9em', width: '100%' }}>
                        <i className="fas fa-link" style={{ fontSize: '0.7em', marginRight: '4px' }}></i>
                        {getWebsiteStatusLabel(websiteStatus, websiteLoading, websiteStockStatus)}
                      </div>
                      {websiteHasDiscount && (
                        <div className="bg-white text-dark rounded-2 px-2 py-1 mb-1" style={{ width: '100%', whiteSpace: 'normal' }}>
                          <div className="d-flex align-items-center justify-content-between gap-2">
                            <span className="small fw-bold">
                              <i className="fa-solid fa-percent me-1 text-danger"></i>
                              Remise site
                            </span>
                            <span className="badge bg-danger">
                              Active
                            </span>
                          </div>
                          <div className="small mt-1">
                            <>
                              <span className="text-muted text-decoration-line-through me-2">
                                {formatPrice(websiteRegularPrice)}
                              </span>
                              <span className="fw-bold text-danger">
                                {formatPrice(websiteSalePrice)}
                              </span>
                            </>
                          </div>
                        </div>
                      )}
                      <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '6px', width: '100%' }}>
                        <button
                          type="button"
                          className={`btn btn-sm ${websiteLoading ? "btn-warning" : websiteInStock ? "btn-success" : "btn-outline-dark"}`}
                          onClick={() => handleWooAvailabilityToggle(product)}
                          disabled={websiteLoading || !websiteStatus?.fetched}
                          style={{ minWidth: '135px' }}
                        >
                          <i className={`fa-solid ${websiteInStock ? "fa-toggle-on" : "fa-toggle-off"} me-2`}></i>
                          {websiteLoading
                            ? "Chargement..."
                            : websiteInStock
                              ? "Desactiver"
                              : "Activer"}
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${websiteFeatured ? "btn-warning" : "btn-outline-dark"}`}
                          onClick={() => handleWooFeaturedToggle(product)}
                          disabled={websiteLoading || !websiteStatus?.fetched}
                          title={websiteFeatured ? "Retirer la mise en avant" : "Mettre en avant"}
                        >
                          <i className={`${websiteFeatured ? "fa-solid" : "fa-regular"} fa-star`}></i>
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${websiteHasDiscount ? "btn-danger" : "btn-outline-dark"}`}
                          onClick={() => openWebsiteDiscountModal(product)}
                          disabled={websiteLoading || !websiteStatus?.fetched}
                          title={websiteHasDiscount ? "Modifier la remise site" : "Appliquer une remise site"}
                          data-bs-toggle="modal"
                          data-bs-target="#websiteDiscountModal"
                        >
                          <i className="fa-solid fa-percent"></i>
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${websiteAddonsCount ? "btn-primary" : "btn-outline-dark"}`}
                          onClick={() => openWebsiteAddonsModal(product)}
                          disabled={websiteLoading || !websiteStatus?.fetched}
                          title="Options produit sur le site"
                          data-bs-toggle="modal"
                          data-bs-target="#websiteAddonsModal"
                        >
                          <i className="fa-solid fa-list-check me-1"></i>
                          {websiteAddonsCount || ""}
                        </button>
                      </div>
                      <div
                        className="bg-light text-dark rounded-2 p-2 mt-2"
                        style={{
                          width: '100%',
                          border: '1px solid rgba(0,0,0,0.08)',
                          whiteSpace: 'normal',
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                          <span className="fw-bold small">
                            <i className="fa-solid fa-truck-fast me-1 text-danger"></i>
                            Livraison
                          </span>
                          <div className="d-flex align-items-center gap-1">
                            {deliveryChanged && (
                              <button
                                type="button"
                                className="btn btn-sm btn-success py-0 px-2"
                                title="Confirmer"
                                onClick={() => saveWebsiteDeliveryDraft(product)}
                                disabled={Boolean(websiteDeliverySaving[deliveryKey])}
                              >
                                <i className={`fa-solid ${websiteDeliverySaving[deliveryKey] ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>
                              </button>
                            )}
                            {deliveryChanged && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary py-0 px-2"
                                title="Annuler"
                                onClick={() => {
                                  setWebsiteDeliveryDrafts((prev) => {
                                    const next = { ...prev };
                                    delete next[deliveryKey];
                                    return next;
                                  });
                                }}
                                disabled={Boolean(websiteDeliverySaving[deliveryKey])}
                              >
                                <i className="fa-solid fa-xmark"></i>
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="d-flex flex-wrap gap-1 mb-2">
                          <button
                            type="button"
                            className={`btn btn-sm py-0 px-2 ${deliveryDraft.express_enabled ? 'btn-danger' : 'btn-outline-danger'}`}
                            title="Livraison express"
                            onClick={() => setWebsiteDeliveryDraft(product, { express_enabled: !deliveryDraft.express_enabled })}
                            disabled={Boolean(websiteDeliverySaving[deliveryKey])}
                          >
                            <i className="fa-solid fa-bolt me-1"></i>
                            Express
                          </button>
                          <button
                            type="button"
                            className={`btn btn-sm py-0 px-2 ${deliveryDraft.morocco_enabled ? 'btn-success' : 'btn-outline-success'}`}
                            title="Livraison partout au Maroc"
                            onClick={() => saveWebsiteDeliveryDirect(product, {
                              ...deliveryDraft,
                              morocco_enabled: !deliveryDraft.morocco_enabled,
                            })}
                            disabled={Boolean(websiteDeliverySaving[deliveryKey])}
                          >
                            <i className={`fa-solid ${websiteDeliverySaving[deliveryKey] ? 'fa-spinner fa-spin' : 'fa-map-location-dot'} me-1`}></i>
                            Maroc
                          </button>
                        </div>
                        {deliveryDraft.express_enabled && (
                          <Select
                            isMulti
                            options={deliveryZoneOptions}
                            value={selectedZones}
                            onChange={(selectedOptions) =>
                              setWebsiteDeliveryDraft(product, {
                                express_enabled: true,
                                express_zones: (selectedOptions || []).map((option) => ({
                                  code: option.value,
                                  name: option.label,
                                })),
                              })
                            }
                            placeholder="Zones"
                            classNamePrefix="select"
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                            menuPosition="fixed"
                            styles={{
                              container: (base) => ({ ...base, width: '100%', minWidth: 0 }),
                              control: (base) => ({ ...base, minHeight: 30, fontSize: 12 }),
                              valueContainer: (base) => ({ ...base, padding: '0 6px' }),
                              indicatorsContainer: (base) => ({ ...base, height: 28 }),
                              multiValue: (base) => ({ ...base, maxWidth: 120 }),
                              menuPortal: (base) => ({ ...base, zIndex: 3000 }),
                            }}
                            isDisabled={Boolean(websiteDeliverySaving[deliveryKey])}
                          />
                        )}
                        {!deliveryDraft.express_enabled && !deliveryDraft.morocco_enabled && (
                          <div className="text-muted small">Aucune option</div>
                        )}
                      </div>
                    </div>
                      );
                    })()
                  ) : null;

                  if (!cards.length && !websiteCard) {
                    return <span className="text-muted small">Aucune donnée roadmap</span>;
                  }

                  return [websiteCard, ...cards.map((card, index) => {
                    const { roadmap, storeName, platformDisplayName, categoryName, isAvailable, borderStyle, storeInfo, roadmapType, parentPlateformId } = card;

                    return (
                      <div 
                        key={`${roadmap.vendorId}-${roadmap.remote_id}-${roadmapType}-${index}`} 
                        className={`badge text-bg-${isAvailable ? "success" : "secondary"} mx-1 mb-1`} 
                        style={{ 
                          fontSize: '0.75rem', 
                          padding: '0.5em 0.65em', 
                          display: 'inline-flex', 
                          flexDirection: 'column', 
                          alignItems: 'flex-start', 
                          maxWidth: '220px',
                          minWidth: '180px',
                          border: borderStyle
                        }}
                      >
                        {/* Store Name */}
                        <div style={{ 
                          fontWeight: '700', 
                          marginBottom: '4px', 
                          width: '100%', 
                          borderBottom: '1px solid rgba(255,255,255,0.3)', 
                          paddingBottom: '3px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}>
                          <span>
                            <i className="fas fa-store" style={{ fontSize: '0.7em', marginRight: '4px' }}></i>
                            {storeName}
                          </span>
                          <span className="badge bg-light text-dark" style={{ fontSize: '0.7em', padding: '2px 5px' }}>
                            {platformDisplayName}
                          </span>
                        </div>
                        
                        {/* Category Name */}
                        <div style={{ marginBottom: '4px', fontSize: '0.9em', width: '100%' }}>
                          <i className="fas fa-tag" style={{ fontSize: '0.7em', marginRight: '4px' }}></i>
                          {categoryName}
                        </div>
                        
                        {/* Availability Checkbox (clickable) */}
                        <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '6px', width: '100%' }}>
                          <input
                            type="checkbox"
                            checked={isAvailable}
                            disabled={Boolean(availabilityToggleLoading[product._id])}
                            onChange={async () => {
                              const productWithContext = {
                                ...product,
                                _selectedVendorId: roadmap.vendorId || roadmap.vendorId || roadmap.vendorIdCandidate,
                                _selectedRemoteId: roadmap.remote_id || roadmap.data?.id,
                                _selectedType: roadmapType,
                                _selectedPlatformId: parentPlateformId,
                                _currentAvailability: isAvailable,
                                _targetAvailability: !isAvailable,
                                _storeInfo: storeInfo
                              };

                              const roadmapTypeLower = String(roadmapType || '').toLowerCase();

                              if (roadmapTypeLower === 'kooul') {
                                const data = roadmap.data || {};
                                const remoteIdVal = roadmap.remote_id || roadmap.data?.id;
                                const parsedId = Number(remoteIdVal) && !isNaN(Number(remoteIdVal)) ? Number(remoteIdVal) : remoteIdVal;
                                const isEnabling = !isAvailable;
                                const endpoint = isEnabling ? '/kooul/product/enable' : '/kooul/product/disable';

                                const payload = {
                                  access_token: data.access_token || data.token || data.accessToken || data.marketplace_access_token || data.marketplaceToken || '',
                                  user_id: data.user_id || data.userId || undefined,
                                  marketplace_user_id: data.marketplace_user_id || data.marketplaceUserId || undefined,
                                  marketplace_reference_id: data.marketplace_reference_id || data.marketplace_reference || data.marketplaceReference || undefined,
                                  user_type: data.user_type || data.userType || undefined,
                                  product_ids: [parsedId],
                                  is_enabled: isEnabling ? 1 : 0
                                };

                                if (!payload.access_token) {
                                  toast.error('Kooul access token missing (cannot toggle)');
                                  return;
                                }

                                try {
                                  setAvailabilityToggleLoading(prev => ({ ...prev, [product._id]: true }));
                                  const resp = await axios.post(endpoint, payload);
                                  toast.success((resp?.data && resp.data.message) || `Kooul ${isEnabling ? 'enabled' : 'disabled'}`);
                                  if (typeof onToggleGlovoAvailability === 'function') {
                                    onToggleGlovoAvailability(productWithContext);
                                  }
                                } catch (err) {
                                  console.error('Kooul toggle error', err);
                                  toast.error('Erreur lors de la mise à jour Kooul');
                                } finally {
                                  setAvailabilityToggleLoading(prev => ({ ...prev, [product._id]: false }));
                                }

                                return;
                              }

                              if (typeof onToggleGlovoAvailability === 'function') {
                                onToggleGlovoAvailability(productWithContext);
                              }
                            }}
                            style={{ cursor: 'pointer', margin: 0 }}
                            title={isAvailable ? "Cliquer pour désactiver" : "Cliquer pour activer"}
                          />
                          <span style={{ fontSize: '0.85em', fontWeight: '600' }}>
                            {isAvailable ? "✓ Activé" : "✗ Désactivé"}
                          </span>
                        </div>
                      </div>
                    );
                  })].filter(Boolean);
                })()}
              </td>
             
              <td>
                <button
                  className="btn btn-light"
                  type="button"
                  onClick={() => setActionsModalProduct(product)}
                  data-bs-toggle="modal"
                  data-bs-target="#actionsModal"
                >
                  <i className="fa-solid fa-ellipsis-vertical"></i>
                </button>
              </td>
            </tr>
            );
          })
            )}
        </tbody>
      </table>
      ) : (
        <div className="row g-3">
          {isLoading ? (
            <div className="col-12">
              <div className="border rounded-3 p-4 text-center text-muted">
                Chargement des produits...
              </div>
            </div>
          ) : displayedProducts.map((product) => {
            const {
              hasGlovo,
              hasKooulLink,
              hasSalePrice,
              hasWebsite,
              regularPrice,
              salePrice,
              selectionActive,
              websiteFeatured,
              websiteHasDiscount,
              websiteInStock,
              websiteLoading,
              websiteRegularPrice,
              websiteSalePrice,
              websiteStatus,
              websiteStockStatus,
            } = getProductComputedState(product);

            return (
              <div key={product._id} className="col-12 col-sm-6 col-xl-3">
                <div
                  className="card h-100 border-0"
                  onClick={() => handleSelectOne(product._id)}
                  style={{
                    cursor: "pointer",
                    borderRadius: "18px",
                    background: selectionActive ? "linear-gradient(180deg, #dbeafe 0%, #eff6ff 100%)" : "#ffffff",
                    boxShadow: selectionActive ? "0 18px 40px rgba(37, 99, 235, 0.24)" : "0 10px 25px rgba(15, 23, 42, 0.08)",
                    outline: selectionActive ? "2px solid #2563eb" : "1px solid rgba(148, 163, 184, 0.24)",
                    color: "#0f172a",
                    transition: "all 0.2s ease",
                  }}
                >
                  <div className="position-relative p-3 pb-0">
                    <img
                      src={resolveImageUrl(product.url_product) || ""}
                      alt={product.name}
                      className="w-100 rounded-3"
                      style={{ height: "220px", objectFit: "contain", backgroundColor: "#f8fafc" }}
                      data-bs-toggle="modal"
                      data-bs-target="#imageModal"
                      onClick={(event) => {
                        event.stopPropagation();
                        openImageModal(product);
                      }}
                    />
                    <button
                      type="button"
                      className={`btn btn-sm ${selectionActive ? "btn-primary" : "btn-outline-primary"} position-absolute top-0 end-0 mt-3 me-3`}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleSelectOne(product._id);
                      }}
                    >
                      <i className={`fa-solid ${selectionActive ? "fa-check" : "fa-plus"} me-1`}></i>
                      {selectionActive ? "Selected" : "Select"}
                    </button>
                  </div>

                  <div className="card-body d-flex flex-column gap-3">
                    {renderVaseSelector(product)}
                    <div>
                      <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                        {hasGlovo && (
                          <img
                            src={GLOVO_LOGO_URL}
                            alt="Glovo"
                            style={{ width: 22, height: 22, borderRadius: "50%", objectFit: "cover" }}
                          />
                        )}
                        {hasKooulLink && (
                          <img
                            src={KOUL_LOGO_URL}
                            alt="Kooul"
                            style={{ width: 22, height: 22, borderRadius: "50%", objectFit: "cover" }}
                          />
                        )}
                        {hasWebsite && (
                          <span className="badge text-bg-info">WooCommerce</span>
                        )}
                        {websiteFeatured && (
                          <span className="badge text-bg-warning">Featured</span>
                        )}
                        {websiteHasDiscount && (
                          <span className="badge text-bg-danger">
                            <i className="fa-solid fa-percent me-1"></i>
                            Remise site
                          </span>
                        )}
                        {renderWebsiteDeliveryBadges(product)}
                      </div>
                      <h6 className="mb-2" style={{ lineHeight: 1.35 }}>{product.name}</h6>
                      <div className="d-flex flex-wrap gap-2">
                        {regularPrice !== null && (
                          <span className={`badge ${hasSalePrice ? "bg-secondary" : "bg-primary"}`}>
                            Regular: {formatPrice(regularPrice)}
                          </span>
                        )}
                        {hasSalePrice && (
                          <span className="badge bg-success">
                            Sale: {formatPrice(salePrice)}
                          </span>
                        )}
                        {hasWebsite && websiteHasDiscount && (
                          <span className="badge bg-danger">
                            Site: {formatPrice(websiteRegularPrice)} → {formatPrice(websiteSalePrice)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="d-grid gap-2">
                      <div className={`rounded-3 px-3 py-2 ${product.availability ? "bg-success-subtle" : "bg-danger-subtle"}`}>
                        <div className="small text-muted">Stock local</div>
                        <div className="fw-semibold">{product.availability ? "Disponible" : "Indisponible"}</div>
                      </div>
                      <div className={`rounded-3 px-3 py-2 ${websiteInStock ? "bg-info-subtle" : "bg-secondary-subtle"}`}>
                        <div className="small text-muted">Stock WooCommerce</div>
                        <div className="fw-semibold">{hasWebsite ? getWebsiteStatusLabel(websiteStatus, websiteLoading, websiteStockStatus) : "Non publie"}</div>
                      </div>
                      <div className={`rounded-3 px-3 py-2 ${product.isComposant ? "bg-warning-subtle" : "bg-light"}`}>
                        <div className="small text-muted">Composant</div>
                        <div className="fw-semibold">{product.isComposant ? "Active" : "Desactive"}</div>
                      </div>
                    </div>

                    <div className="mt-auto d-grid gap-2">
                      <button
                        type="button"
                        className={`btn btn-sm ${product.availability ? "btn-success" : "btn-outline-danger"}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          updateGlobalAvailability(product._id, !product.availability);
                        }}
                      >
                        <i className={`fa-solid ${product.availability ? "fa-toggle-on" : "fa-toggle-off"} me-2`}></i>
                        {product.availability ? "Disable Local Stock" : "Enable Local Stock"}
                      </button>
                      {hasWebsite && (
                        <button
                          type="button"
                          className={`btn btn-sm ${websiteLoading ? "btn-warning" : websiteInStock ? "btn-info" : "btn-outline-info"}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleWooAvailabilityToggle(product);
                          }}
                          disabled={websiteLoading || !websiteStatus?.fetched}
                        >
                          <i className={`fa-solid ${websiteInStock ? "fa-toggle-on" : "fa-toggle-off"} me-2`}></i>
                          {websiteLoading ? "Chargement..." : websiteInStock ? "Disable Woo Stock" : "Enable Woo Stock"}
                        </button>
                      )}
                      {hasWebsite && (
                        <button
                          type="button"
                          className={`btn btn-sm ${websiteHasDiscount ? "btn-danger" : "btn-outline-danger"}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            openWebsiteDiscountModal(product);
                          }}
                          disabled={websiteLoading || !websiteStatus?.fetched}
                          data-bs-toggle="modal"
                          data-bs-target="#websiteDiscountModal"
                        >
                          <i className="fa-solid fa-percent me-2"></i>
                          {websiteHasDiscount ? "Modifier remise site" : "Appliquer remise site"}
                        </button>
                      )}
                      <button
                        type="button"
                        className={`btn btn-sm ${product.isComposant ? "btn-warning" : "btn-outline-secondary"}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleIsComponentToggle(product, !Boolean(product.isComposant));
                        }}
                        disabled={componentToggleLoading[product._id]}
                      >
                        <i className={`fa-solid ${product.isComposant ? "fa-toggle-on" : "fa-toggle-off"} me-2`}></i>
                        {product.isComposant ? "Disable Component" : "Enable Component"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        modalId="websiteDiscountModal"
        title="Remise Site Web"
        submitLabel={websiteDiscountSaving ? "Application..." : "Appliquer la remise"}
        submitDisabled={websiteDiscountSaving}
        onSubmit={() => saveWebsiteDiscount()}
        onClose={() => {
          if (!websiteDiscountSaving) {
            setWebsiteDiscountProduct(null);
          }
        }}
      >
        {websiteDiscountProduct && (
          <div className="d-grid gap-3">
            <div className="p-3 rounded-3 bg-light border">
              <div className="fw-bold">{websiteDiscountProduct.name}</div>
              <div className="small text-muted">
                WooCommerce ID: {websiteDiscountProduct.woocommerce_id || "-"}
              </div>
            </div>

            <div className="row g-3">
              <div className="col-12 col-md-4">
                <label className="form-label fw-semibold">Prix avant remise</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-control"
                  value={websiteDiscountForm.regularPrice}
                  onChange={(event) =>
                    setWebsiteDiscountForm((prev) => ({
                      ...prev,
                      regularPrice: event.target.value,
                      percentage: calculateDiscountPercent(event.target.value, prev.salePrice),
                    }))
                  }
                />
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label fw-semibold">Remise (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  className="form-control"
                  value={websiteDiscountForm.percentage}
                  onChange={handleWebsiteDiscountPercentageChange}
                  placeholder="Ex: 15"
                />
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label fw-semibold">Prix après remise</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-control"
                  value={websiteDiscountForm.salePrice}
                  onChange={handleWebsiteDiscountSalePriceChange}
                  placeholder="Prix soldé"
                />
              </div>
            </div>

            <div className="alert alert-info mb-0">
              <i className="fa-solid fa-circle-info me-2"></i>
              Vous pouvez saisir soit un pourcentage, soit directement le prix après remise. Le produit WooCommerce sera mis à jour avec ce prix soldé.
            </div>

            <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => saveWebsiteDiscount({ clear: true })}
                disabled={websiteDiscountSaving}
              >
                <i className="fa-solid fa-ban me-2"></i>
                Retirer la remise
              </button>
              <div className="fw-semibold">
                Aperçu:{" "}
                {parsePrice(websiteDiscountForm.salePrice) !== null ? (
                  <>
                    <span className="text-muted text-decoration-line-through me-2">
                      {formatPrice(websiteDiscountForm.regularPrice)}
                    </span>
                    <span className="text-danger">
                      {formatPrice(websiteDiscountForm.salePrice)}
                    </span>
                  </>
                ) : (
                  <span className="text-muted">Aucune remise</span>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        modalId="websiteAddonsModal"
        title="Options Site Web"
        submitLabel={websiteAddonsSaving ? "Synchronisation..." : "Synchroniser"}
        submitDisabled={websiteAddonsSaving || !websiteAddonsProduct?.woocommerce_id}
        onSubmit={saveWebsiteAddons}
        onClose={() => {
          if (!websiteAddonsSaving) {
            setWebsiteAddonsProduct(null);
            setSelectedWebsiteAddonIds([]);
          }
        }}
      >
        {websiteAddonsProduct && (
          <div className="d-grid gap-3">
            <div className="p-3 rounded-3 bg-light border">
              <div className="fw-bold">{websiteAddonsProduct.name}</div>
              <div className="small text-muted">
                WooCommerce ID: {websiteAddonsProduct.woocommerce_id || "-"}
              </div>
            </div>

            {Array.isArray(productAddons) && productAddons.length > 0 ? (
              <div className="list-group">
                {productAddons.map((addon) => {
                  const addonId = normalizeId(addon?._id || addon?.id);
                  const checked = selectedWebsiteAddonIds.includes(addonId);
                  const typeLabel = addon?.input_type || addon?.type || "select";
                  return (
                    <label
                      key={addonId}
                      className="list-group-item d-flex align-items-start gap-3"
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input mt-1"
                        checked={checked}
                        onChange={(event) => {
                          setSelectedWebsiteAddonIds((prev) => {
                            if (event.target.checked) {
                              return Array.from(new Set([...prev, addonId]));
                            }
                            return prev.filter((id) => id !== addonId);
                          });
                        }}
                        disabled={websiteAddonsSaving}
                      />
                      <span className="flex-grow-1">
                        <span className="d-flex align-items-center justify-content-between gap-2">
                          <strong>{addon.name}</strong>
                          <span className="badge bg-secondary">{typeLabel}</span>
                        </span>
                        <span className="small text-muted d-block">
                          {(addon.options || []).length} option(s)
                          {addon.required ? " · obligatoire" : ""}
                          {addon.multiple ? " · multi-sélection" : ""}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="alert alert-warning mb-0">
                Aucun groupe d'options Pegasus disponible.
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Actions Modal */}
      <Modal modalId="actionsModal" title="Actions">
        {actionsModalProduct && (() => {
          const product = actionsModalProduct;
          const productPlateformIds = getProductPlateformIds(product);
          const productPlateformEntries = Array.isArray(product?.plateforms) ? product.plateforms : [];
          const productGlovoPlateformIds = getProductGlovoFamilyPlateformIds(product);

          const hasGlovo = glovoPlateformIds.size
            ? Array.from(productPlateformIds).some((plateformId) => glovoPlateformIds.has(plateformId))
            : productGlovoPlateformIds.size
              ? productGlovoPlateformIds.size > 0
              : Array.isArray(product?.glovo_id)
                ? product.glovo_id.length > 0
                : !!product?.glovo_id;
          const glovoExternalId = Array.isArray(product?.glovo_id)
            ? product.glovo_id[0]
            : product?.glovo_id;
          const productKooulIds = getProductKooulPlateformIds(product);
          const hasKooulLink = kooulPlateformIds.some((plateformId) => productKooulIds.has(plateformId));
          const kooulButtonDisabled = getKooulButtonDisabled(product);
          const aimerPlateformEntry = productPlateformEntries.find((entry) => {
            const plateformId = normalizeId(entry?.plateform_id || entry?._id || entry?.id || entry?.value);
            return plateformId && aimerPlateformIds.includes(plateformId);
          });
          const aimerRemoteId = aimerPlateformEntry
            ? normalizeId(
                aimerPlateformEntry.remote_id ||
                  aimerPlateformEntry.remoteId ||
                  aimerPlateformEntry.remote_product_id ||
                  aimerPlateformEntry.remoteProductId ||
                  aimerPlateformEntry.external_id ||
                  aimerPlateformEntry.externalId ||
                  aimerPlateformEntry.product_id ||
                  aimerPlateformEntry.productId,
              )
            : '';
          const hasAimerLink = Boolean(aimerPlateformEntry);
          const aimerButtonDisabled = !aimerPlateformIds.length;
          const canManageAimerStatus = hasAimerLink && Boolean(aimerRemoteId);

          return (
            <div className="d-grid gap-2">
              <button
                className="btn btn-outline-dark w-100"
                type="button"
                onClick={() => {
                  closeBootstrapModal('actionsModal');
                  navigate(`/product-sales-statistics?productId=${encodeURIComponent(normalizeId(product._id))}`, {
                    state: {
                      products: fullProducts.length ? fullProducts : products,
                      selectedProductId: normalizeId(product._id),
                    },
                  });
                }}
              >
                <i className="fa-solid fa-chart-line me-2"></i>
                Références de vente
              </button>
              {typeof onOpenPublierPlateformes === 'function' && (
                <button
                  className="btn btn-success w-100"
                  type="button"
                  onClick={() => {
                    onOpenPublierPlateformes(product);
                  }}
                >
                  <i className="fa-solid fa-rocket me-2"></i>
                  Publier sur les Plateformes
                </button>
              )}
              {typeof onAddStock === 'function' && (
                <button
                  className="btn btn-outline-primary w-100"
                  data-bs-toggle="modal"
                  data-bs-target="#stockMovementModal"
                  onClick={() => {
                    onAddStock(product);
                    document.querySelector('[data-bs-dismiss="modal"]')?.click();
                  }}
                >
                  <i className="fa-solid fa-box me-2"></i>
                  Ajouter du stock
                </button>
              )}
              {typeof onPrintBarcode === 'function' && (
                <button
                  className="btn btn-outline-secondary w-100"
                  type="button"
                  onClick={() => {
                    onPrintBarcode(product);
                    document.querySelector('[data-bs-dismiss="modal"]')?.click();
                  }}
                >
                  <i className="fa-solid fa-print me-2"></i>
                  Imprimer le code-barres
                </button>
              )}
              {typeof onManageStoreExpress === 'function' && (
                <button
                  className="btn btn-outline-success w-100"
                  type="button"
                  onClick={() => {
                    closeBootstrapModal('actionsModal');
                    window.setTimeout(() => onManageStoreExpress(product), 180);
                  }}
                >
                  <i className="fa-solid fa-store me-2"></i>
                  Store Express
                </button>
              )}
              <button
                className="btn btn-outline-warning w-100"
                type="button"
                data-bs-toggle="modal"
                data-bs-target="#pluCarrefourModal"
                onClick={() => {
                  openPluModal(product);
                  document.querySelector('[data-bs-dismiss="modal"]')?.click();
                }}
              >
                <i className="fa-solid fa-barcode me-2"></i>
                PLU Carrefour
              </button>
              {hasAimerLink && typeof onManageAimerStatus === 'function' ? (
                <button
                  className="btn btn-outline-warning w-100"
                  type="button"
                  onClick={() => {
                    onManageAimerStatus(product, aimerRemoteId);
                    document.querySelector('[data-bs-dismiss="modal"]')?.click();
                  }}
                  disabled={!canManageAimerStatus}
                >
                  <i className="fa-solid fa-toggle-on me-2"></i>
                  Statut Aimer
                </button>
              ) : null}
              <button
                className="btn btn-outline-primary w-100"
                data-bs-toggle="modal"
                data-bs-target="#crudModal"
                onClick={() => {
                  handleEditClick(product);
                  document.querySelector('[data-bs-dismiss="modal"]')?.click();
                }}
              >
                <i className="fa-solid fa-pen-to-square me-2"></i>
                Modifier
              </button>
              {typeof handleCloneProduct === 'function' && (
                <button
                  className="btn btn-outline-dark w-100"
                  data-bs-toggle="modal"
                  data-bs-target="#crudModal"
                  onClick={() => {
                    handleCloneProduct(product);
                    document.querySelector('[data-bs-dismiss="modal"]')?.click();
                  }}
                >
                  <i className="fa-solid fa-copy me-2"></i>
                  Cloner
                </button>
              )}
              <button
                className="btn btn-outline-danger w-100"
                onClick={() => {
                  handleDelete(product._id);
                  document.querySelector('[data-bs-dismiss="modal"]')?.click();
                }}
              >
                <i className="fa-solid fa-trash me-2"></i>
                Supprimer
              </button>
              <button
                className="btn btn-outline-info w-100"
                onClick={() => {
                  setCurrentProduct(product);
                  document.querySelector('[data-bs-dismiss="modal"]')?.click();
                }}
                data-bs-toggle="modal"
                data-bs-target="#exampleModal"
              >
                <i className="fa-solid fa-circle-info me-2"></i>
                Détails
              </button>
              {hasGlovo && typeof onToggleGlovoAvailability === 'function' && (
                <button
                  className="btn btn-outline-secondary w-100"
                  type="button"
                  onClick={() => {
                    onToggleGlovoAvailability(product);
                    document.querySelector('[data-bs-dismiss="modal"]')?.click();
                  }}
                  data-bs-toggle="modal"
                  data-bs-target="#glovoToggleProductModal"
                >
                  <i className="fa-solid fa-toggle-on me-2"></i>
                  Gérer disponibilité Glovo
                </button>
              )}
              <button
                className="btn btn-outline-info w-100"
                data-bs-toggle="modal"
                onClick={() => {
                  handleComponent(product);
                  document.querySelector('[data-bs-dismiss="modal"]')?.click();
                }}
                data-bs-target="#componentOfProducts"
              >
                <i className="fa-solid fa-layer-group me-2"></i>
                Composants
              </button>
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  openProcessModal(product);
                  document.querySelector('[data-bs-dismiss="modal"]')?.click();
                }}
                data-bs-toggle="modal"
                data-bs-target="#processImageModal"
              >
                <i className="fa-solid fa-image me-2"></i>
                Retraiter l'image
              </button>
            </div>
          );
        })()}
      </Modal>


      {/* Pagination Controls */}
      <nav className="mt-4">
        <ul className="pagination justify-content-center">
          {/* First Page */}
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => setCurrentPage(1)}>
              First
            </button>
          </li>

          {/* Previous Page */}
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>
              Previous
            </button>
          </li>

          {/* Page Numbers */}
          {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((page) => (
            <li key={page} className={`page-item ${page === currentPage ? "active" : ""}`}>
              <button className="page-link" onClick={() => setCurrentPage(page)}>
                {page}
              </button>
            </li>
          ))}

          {/* Next Page */}
          <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>
              Next
            </button>
          </li>

          {/* Last Page */}
          <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => setCurrentPage(totalPages)}>
              Last
            </button>
          </li>
        </ul>
      </nav>

      <Modal modalId="imageModal" modalType="modal-lg">
        <div className="text-center">
          {modalImage && (
            <img
              src={modalImage}
              alt="Zoomed"
              className="img-fluid rounded"
              style={{ maxHeight: "72vh", objectFit: "contain" }}
            />
          )}
          {modalImages.length > 1 && (
            <div className="d-flex flex-wrap justify-content-center mt-3">
              {modalImages.map((image, index) => (
                <button
                  key={`${image}-${index}`}
                  type="button"
                  className="btn p-0 bg-transparent border-0"
                  onClick={() => setModalImage(image)}
                >
                  <img
                    src={image}
                    alt={`Galerie ${index + 1}`}
                    width={80}
                    className={`me-2 mb-2 rounded ${modalImage === image ? 'border border-primary' : 'border'}`}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </Modal>

      <Modal
        modalId="processImageModal"
        title="Reprocess Image"
        submitLabel="Envoyer"
        onSubmit={handleProcessImage}
      >
        <div className="mb-3">
          <label className="form-label">Variables</label>
          <Select
            classNamePrefix="select"
            options={variableOptions}
            value={selectedVariableOption}
            onChange={handleVariableChange}
            isClearable
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Prompt additionnel</label>
          <textarea
            className="form-control"
            value={additionnalPrompt}
            onChange={(e) => setAdditionnalPrompt(e.target.value)}
            placeholder="Texte optionnel pour guider le traitement"
          />
        </div>
        {processProduct?.additionnal_data?.prompt_variables?.length ? (
          <div className="mb-3">
            <label className="form-label">Valeurs des variables du prompt</label>
            <div className="row g-3">
              {processProduct.additionnal_data.prompt_variables.map((entry) => (
                <div className="col-md-6" key={entry.key}>
                  <label className="form-label">
                    {entry.label || entry.key}
                    <small className="text-muted ms-2">
                      {entry.type || "string"}
                    </small>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={entry.value || ""}
                    placeholder={entry.default_value || `Valeur pour ${entry.key}`}
                    onChange={(e) => handlePromptVariableChange(entry.key, e.target.value)}
                  />
                  {entry.default_value ? (
                    <small className="text-muted">
                      Valeur par défaut: {entry.default_value}
                    </small>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}
        <div className="mb-3">
          <label className="form-label">Type</label>
          <select
            className="form-select"
            value={imageProcessingType}
            onChange={(e) => setImageProcessingType(e.target.value)}
          >
            {imageProcessingTypeOptions.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        <div className="d-flex flex-wrap">
          {variables
            .filter(
              (v) => selectedVariableIds.includes(v._id || v.id) && v.src
            )
            .map((v) => (
              <img
                key={v._id || v.id}
                src={`${process.env.REACT_APP_PEGASUS_API_URL}${v.src}`}
                width={80}
                className="me-2 mb-2"
                alt="variable"
              />
            ))}
        </div>
      </Modal>

      {/* PLU Carrefour Modal */}
      <Modal 
        modalId="pluCarrefourModal" 
        title="Générer PLU Carrefour"
        submitLabel="Générer et Imprimer"
        onSubmit={generatePluBarcode}
      >
        {pluModalProduct && (
          <div>
            <div className="mb-3">
              <h6>{pluModalProduct.title}</h6>
              <p className="text-muted">
                Prix produit: {parseFloat(pluModalProduct.sale_price || pluModalProduct.regular_price || 0).toFixed(2)} DH
              </p>
            </div>

            <div className="mb-3 form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="useCustomPluPrice"
                checked={useCustomPluPrice}
                onChange={(e) => setUseCustomPluPrice(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="useCustomPluPrice">
                Utiliser un prix personnalisé
              </label>
            </div>

            {useCustomPluPrice && (
              <div className="mb-3">
                <label className="form-label">Prix personnalisé</label>
                <input
                  type="number"
                  min="0"
                  max="999.99"
                  step="0.01"
                  className="form-control"
                  value={customPluPrice}
                  onChange={(e) => setCustomPluPrice(e.target.value)}
                />
              </div>
            )}

            <div className="mb-3">
              <label className="form-label">
                Catégorie PLU <span className="text-danger">*</span>
              </label>
              {pluCategories.length > 0 ? (
                <select
                  className="form-select"
                  value={selectedPluCategory ? selectedPluCategory.category : ''}
                  onChange={(e) => {
                    const cat = pluCategories.find(c => c.category === e.target.value);
                    setSelectedPluCategory(cat || null);
                  }}
                >
                  <option value="">-- Sélectionner une catégorie --</option>
                  {pluCategories.map((cat, idx) => (
                    <option key={idx} value={cat.category}>
                      {cat.category} (PLU: {cat.plu})
                    </option>
                  ))}
                </select>
              ) : (
                <div className="alert alert-warning">
                  Aucune catégorie PLU configurée. Veuillez configurer les variables PLU.
                </div>
              )}
            </div>

            {selectedPluCategory && (() => {
              const price = getEffectivePluPrice();
              if (!Number.isFinite(price) || price <= 0) {
                return (
                  <div className="alert alert-warning">
                    Veuillez saisir un prix valide pour générer le PLU.
                  </div>
                );
              }
              const integerPart = Math.floor(price);
              const decimalPart = Math.round((price - integerPart) * 100);
              const integerStr = String(integerPart).padStart(3, '0');
              const decimalStr = String(decimalPart).padStart(2, '0') + '0';
              const priceStr = integerStr + decimalStr;
              
              return (
                <div className="alert alert-info">
                  <strong>Aperçu:</strong>
                  <div>PLU: {selectedPluCategory.plu}</div>
                  <div>
                    Prix encodé: {priceStr} (Integer: {integerStr}, Decimal: {decimalStr})
                  </div>
                  <div>
                    <strong>QR Code final (23mm):</strong> {selectedPluCategory.plu + priceStr}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </Modal>

    </div>
  );
};

export default ProductTable;
